import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FloppyDisk,
  Plus,
  Trash,
  ArrowUp,
  ArrowDown,
  DotsSixVertical,
  UploadSimple,
  DownloadSimple,
  Globe,
  Sparkle,
  CaretDown,
  Gear,
  House,
  Article,
  SquaresFour,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { ContactTilesEditor } from "@/components/ContactTilesEditor";
import { ResourcesEditor } from "@/components/ResourcesEditor";
import { RichTextEditor } from "@/components/RichTextEditor";
import { EdgeProfilesEditor } from "@/components/EdgeProfilesEditor";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { HOME_SECTIONS, sectionOrder } from "@/lib/siteContent";

const SECTION_META = [
  { id: "general", title: "Site live", tab: "general", testId: "site-general", kw: "enable disable coming soon" },
  { id: "branding", title: "Header logo", tab: "general", testId: "site-branding", kw: "logo brand" },
  { id: "seo", title: "Search & sharing", tab: "general", testId: "site-seo", kw: "seo meta og social preview google title description" },
  { id: "analytics", title: "Analytics", tab: "general", testId: "site-analytics", kw: "tracking google tag pixel gtm" },
  { id: "google", title: "Google tools", tab: "general", testId: "site-google", kw: "google search console verification business profile place id ads sitemap robots structured data schema maps" },
  { id: "order", title: "Home page order", tab: "home", testId: "site-order", kw: "reorder blocks sections" },
  { id: "hero", title: "Hero", tab: "home", testId: "site-hero", kw: "headline banner top video" },
  { id: "stats", title: "Counters", tab: "home", testId: "site-stats", kw: "reviews stock counts rating numbers" },
  { id: "founder_quote", title: "Introduction", tab: "home", testId: "site-founder-quote-editor", kw: "owner founder about photo story intro" },
  { id: "materials", title: "Surfaces carousel", tab: "home", testId: "site-materials", kw: "stone tiles granite quartz materials surfaces" },
  { id: "location", title: "Showroom location", tab: "home", testId: "site-location", kw: "map address visit showroom directions" },
  { id: "workflow", title: "How we work", tab: "home", testId: "site-workflow", kw: "steps process" },
  { id: "featured", title: "Slab carousel", tab: "home", testId: "site-featured", kw: "new arrivals deals inventory" },
  { id: "kitchen_tour", title: "3D kitchen tour", tab: "home", testId: "site-kitchen-tour", kw: "3d tour video matterport youtube slider taj mahal quartzite kitchen embed" },
  { id: "why", title: "Three reasons", tab: "home", testId: "site-why", kw: "selling points" },
  { id: "services", title: "Image + ticker band", tab: "home", testId: "site-services", kw: "offerings" },
  { id: "suppliers", title: "Brands we stock", tab: "home", testId: "site-suppliers", kw: "suppliers brands logos" },
  { id: "brands", title: "Collections — Brands", tab: "home", testId: "site-brands-cms", kw: "brand pages collections" },
  { id: "gallery", title: "Featured work list", tab: "home", testId: "site-gallery", kw: "photos images" },
  { id: "reflect", title: "Designs reflect you", tab: "home", testId: "site-reflect", kw: "custom guarantees checklist icon cards designs reflect who you are" },
  { id: "appearance", title: "Appearance", tab: "home", testId: "site-appearance", kw: "theme dark light background color color section spacing margin padding" },
  { id: "testimonials", title: "Testimonials", tab: "home", testId: "site-testimonials", kw: "reviews ratings quotes google" },
  { id: "cta", title: "Contact / quote form", tab: "home", testId: "site-cta", kw: "contact bottom form" },
  { id: "about", title: "About page", tab: "pages", testId: "site-about", kw: "story team history company" },
  { id: "contact", title: "Contact page", tab: "pages", testId: "site-contact", kw: "phone email address tiles map hours company" },
  { id: "resources", title: "Resources page", tab: "pages", testId: "site-resources", kw: "warranty documents pdf manufacturer care guide downloads company resources" },
  { id: "faqs", title: "FAQs", tab: "pages", testId: "site-faqs", kw: "faq questions answers accordion help resources common" },
  { id: "inventory", title: "Inventory categories", tab: "pages", testId: "site-inventory", kw: "inventory categories material images cards granite marble quartz photos" },
  { id: "edges", title: "Edges page — 3D stone", tab: "pages", testId: "site-edges", kw: "edge profiles 3d taj mahal quartzite stone photo texture collections" },
  { id: "footer", title: "Footer", tab: "footer", testId: "site-footer", kw: "bottom links legal socials columns" },
];

const SECTION_TABS = Object.fromEntries(
  SECTION_META.map((s) => [s.id, s.tab]),
);

const TAB_DEFS = [
  { id: "general", label: "General & SEO", icon: Gear, first: "general" },
  { id: "home", label: "Home page", icon: House, first: "hero" },
  { id: "pages", label: "Company pages", icon: Article, first: "about" },
  { id: "footer", label: "Footer", icon: SquaresFour, first: "footer" },
];

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    {children}
    {hint && <span className="mt-1 block text-xs text-zinc-600">{hint}</span>}
  </label>
);

const Section = ({
  title,
  sub,
  testId,
  right,
  children,
  open,
  onToggle,
  hidden,
}) =>
  hidden ? null : (
  <section
    className="rounded-lg border border-zinc-800 bg-zinc-900"
    data-testid={testId}
  >
    <div className="flex w-full items-center gap-3 p-5">
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={onToggle}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <span className="min-w-0">
          <span className="block text-xs font-bold uppercase tracking-widest text-zinc-300">
            {title}
          </span>
          {sub && (
            <span className="mt-1 block text-xs text-zinc-600">{sub}</span>
          )}
        </span>
        <CaretDown
          size={14}
          weight="bold"
          className={`ml-auto shrink-0 text-zinc-500 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {right}
    </div>
    {open && (
      <div className="space-y-4 border-t border-zinc-800 p-5">{children}</div>
    )}
  </section>
);

const Toggle = ({ id, value, onChange, label = "Show on site" }) => (
  <span className="flex shrink-0 items-center gap-2">
    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
      {label}
    </span>
    <Switch
      data-testid={`site-toggle-${id}`}
      checked={!!value}
      onCheckedChange={onChange}
    />
  </span>
);

const ImageField = ({ label, value, onChange, testId, contain = false }) => {
  const [dead, setDead] = useState(false);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [painting, setPainting] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/settings/site/image", form);
      onChange(data.path);
      toast.success("Picture uploaded");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
    setBusy(false);
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setPainting(true);
    try {
      const { data } = await api.post("/ai-images/generate", {
        prompt: prompt.trim(),
        purpose: "website",
      });
      onChange(data.item.path);
      setAsking(false);
      toast.success("Picture generated — save the page to publish it");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not generate that picture",
      );
    }
    setPainting(false);
  };

  const preview = value
    ? value.startsWith("http")
      ? value
      : `${API}/${
          value.includes("/ai-images/")
            ? "ai-images/image"
            : "public/site/image"
        }/${value}`
    : "";

  return (
    <div>
      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
        {label}
      </span>
      <div className="mt-1 flex gap-3">
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md border border-zinc-800 bg-zinc-950">
          {preview && !dead ? (
            <img
              src={preview}
              alt=""
              onLoad={() => setDead(false)}
              onError={() => setDead(true)}
              data-testid={`${testId}-preview`}
              className={`h-full w-full ${contain ? "object-contain p-2" : "object-cover"}`}
            />
          ) : (
            <span
              className="flex h-full items-center justify-center px-1 text-center text-[9px] uppercase tracking-widest text-zinc-600"
              data-testid={`${testId}-placeholder`}
            >
              {dead ? "Photo missing — upload again" : "Default"}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <Input
            data-testid={testId}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste an image link, or upload"
            className="h-10 border-zinc-800 bg-zinc-950 text-xs"
          />
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid={`${testId}-file`}
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <Button
              type="button"
              data-testid={`${testId}-upload`}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="h-9 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
            >
              <UploadSimple size={13} weight="bold" className="mr-2" />
              {busy ? "Uploading…" : "Upload"}
            </Button>
            <Button
              type="button"
              data-testid={`${testId}-generate`}
              onClick={() => setAsking((v) => !v)}
              className="h-9 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-indigo-300 hover:bg-zinc-800"
            >
              <Sparkle size={13} weight="bold" className="mr-2" /> Generate
            </Button>
            {value && (
              <Button
                type="button"
                data-testid={`${testId}-clear`}
                onClick={() => onChange("")}
                className="h-9 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-red-300"
              >
                Use default
              </Button>
            )}
          </div>
          {asking && (
            <div className="space-y-2" data-testid={`${testId}-generate-panel`}>
              <Textarea
                data-testid={`${testId}-prompt`}
                rows={2}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={`Describe the ${label.toLowerCase()} — e.g. "a bright kitchen with a book-matched marble island, oak cabinets, morning light"`}
                className="border-zinc-800 bg-zinc-950 text-xs"
              />
              <Button
                type="button"
                data-testid={`${testId}-generate-go`}
                disabled={painting || !prompt.trim()}
                onClick={generate}
                className="h-9 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {painting ? "Painting… (up to 40s)" : "Make the picture"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CardRows = ({ items, onChange, testId, fields = ["title", "body"] }) => {
  const move = (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const patch = (i, key, value) =>
    onChange(items.map((x, n) => (n === i ? { ...x, [key]: value } : x)));

  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((item, i) => (
        <div
          key={`${testId}-${i}`}
          className="rounded-md border border-zinc-800 bg-zinc-950 p-4"
          data-testid={`${testId}-row-${i}`}
        >
          <div className="flex items-center gap-2">
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-600">
              {i + 1}
            </span>
            <Input
              data-testid={`${testId}-title-${i}`}
              value={item.title || ""}
              onChange={(e) => patch(i, "title", e.target.value)}
              placeholder="Title"
              className="h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
            <button
              type="button"
              data-testid={`${testId}-up-${i}`}
              onClick={() => move(i, -1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowUp size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`${testId}-down-${i}`}
              onClick={() => move(i, 1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowDown size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`${testId}-remove-${i}`}
              onClick={() => onChange(items.filter((_, n) => n !== i))}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"
            >
              <Trash size={13} weight="bold" />
            </button>
          </div>
          {fields.includes("body") && (
            <Textarea
              data-testid={`${testId}-body-${i}`}
              rows={2}
              value={item.body || ""}
              onChange={(e) => patch(i, "body", e.target.value)}
              placeholder="One or two lines of copy"
              className="mt-2 border-zinc-800 bg-zinc-900 text-sm"
            />
          )}
          {fields.includes("category") && (
            <Input
              data-testid={`${testId}-category-${i}`}
              value={item.category || ""}
              onChange={(e) => patch(i, "category", e.target.value)}
              placeholder="Inventory material to link to (e.g. Quartzite)"
              className="mt-2 h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
          )}
          {fields.includes("source") && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Input
                data-testid={`${testId}-rating-${i}`}
                value={item.rating || ""}
                onChange={(e) => patch(i, "rating", e.target.value)}
                placeholder="Stars, e.g. 5"
                className="h-10 border-zinc-800 bg-zinc-900 text-sm"
              />
              <Input
                data-testid={`${testId}-source-${i}`}
                value={item.source || ""}
                onChange={(e) => patch(i, "source", e.target.value)}
                placeholder="Where it came from, e.g. Google"
                className="h-10 border-zinc-800 bg-zinc-900 text-sm"
              />
            </div>
          )}
          {fields.includes("tag") && (
            <Input
              data-testid={`${testId}-tag-${i}`}
              value={item.tag || ""}
              onChange={(e) => patch(i, "tag", e.target.value)}
              placeholder="Small tag (e.g. New, Made in USA)"
              className="mt-2 h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
          )}
          {fields.includes("href") && (
            <Input
              data-testid={`${testId}-href-${i}`}
              value={item.href || ""}
              onChange={(e) => patch(i, "href", e.target.value)}
              placeholder="Link URL (e.g. /resources)"
              className="mt-2 h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
          )}
          {fields.includes("cta") && (
            <Input
              data-testid={`${testId}-cta-${i}`}
              value={item.cta || ""}
              onChange={(e) => patch(i, "cta", e.target.value)}
              placeholder="Button label (e.g. Resources)"
              className="mt-2 h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
          )}
          {fields.includes("image") && (
            <div className="mt-3">
              <ImageField
                label="Picture"
                testId={`${testId}-image-${i}`}
                value={item.image}
                onChange={(v) => patch(i, "image", v)}
              />
            </div>
          )}
        </div>
      ))}
      <Button
        type="button"
        data-testid={`${testId}-add`}
        onClick={() => onChange([...items, { title: "", body: "", image: "" }])}
        className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
      >
        <Plus size={13} weight="bold" className="mr-2" /> Add
      </Button>
    </div>
  );
};

const InventoryCategoriesEditor = ({ value = {}, onChange }) => {
  const [mats, setMats] = useState([]);
  useEffect(() => {
    api
      .get("/public/inventory-categories")
      .then(({ data }) => setMats(data.materials || []))
      .catch(() => setMats([]));
  }, []);
  const key = (n) => n.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const rows = [
    { name: "All types", id: "__all__", slug: "all", count: mats.reduce((n, m) => n + m.count, 0) },
    ...mats.map((m) => ({ name: m.name, id: m.name, slug: key(m.name), count: m.count })),
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="site-inventory-categories">
      {rows.map((r) => (
        <div key={r.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`site-cat-${r.slug}`}>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-zinc-200">{r.name}</span>
            <span className="text-[11px] text-zinc-500">{r.count} in stock</span>
          </div>
          <ImageField
            label=""
            testId={`site-cat-img-${r.slug}`}
            value={value[r.id] || ""}
            onChange={(v) => onChange({ ...value, [r.id]: v })}
          />
        </div>
      ))}
      {mats.length === 0 && (
        <p className="text-xs text-zinc-500">Categories appear here once slabs are in stock.</p>
      )}
    </div>
  );
};

const FaqGroupsEditor = ({ groups = [], onChange }) => {
  const patchGroup = (gi, next) => onChange(groups.map((g, i) => (i === gi ? next : g)));
  const moveGroup = (gi, dir) => {
    const next = [...groups];
    const j = gi + dir;
    if (j < 0 || j >= next.length) return;
    [next[gi], next[j]] = [next[j], next[gi]];
    onChange(next);
  };
  const addGroup = () => onChange([...groups, { title: "New group", items: [{ q: "", a: "" }] }]);
  const removeGroup = (gi) => onChange(groups.filter((_, i) => i !== gi));
  const patchItem = (gi, ii, key, val) =>
    patchGroup(gi, { ...groups[gi], items: (groups[gi].items || []).map((it, i) => (i === ii ? { ...it, [key]: val } : it)) });
  const addItem = (gi) => patchGroup(gi, { ...groups[gi], items: [...(groups[gi].items || []), { q: "", a: "" }] });
  const removeItem = (gi, ii) =>
    patchGroup(gi, { ...groups[gi], items: (groups[gi].items || []).filter((_, i) => i !== ii) });
  const moveItem = (gi, ii, dir) => {
    const items = [...(groups[gi].items || [])];
    const j = ii + dir;
    if (j < 0 || j >= items.length) return;
    [items[ii], items[j]] = [items[j], items[ii]];
    patchGroup(gi, { ...groups[gi], items });
  };
  const iconBtn = "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100";

  return (
    <div className="space-y-4" data-testid="site-faqs-groups">
      {groups.map((g, gi) => (
        <div key={gi} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4" data-testid={`site-faq-group-${gi}`}>
          <div className="flex items-center gap-2">
            <span className="font-mono-data shrink-0 text-[10px] uppercase tracking-widest text-zinc-600">G{gi + 1}</span>
            <Input
              data-testid={`site-faq-group-${gi}-title`}
              value={g.title || ""}
              onChange={(e) => patchGroup(gi, { ...g, title: e.target.value })}
              placeholder="Group title (e.g. Buying & quotes)"
              className="h-10 border-zinc-800 bg-zinc-900 text-sm font-semibold"
            />
            <button type="button" data-testid={`site-faq-group-${gi}-up`} onClick={() => moveGroup(gi, -1)} className={iconBtn}><ArrowUp size={13} weight="bold" /></button>
            <button type="button" data-testid={`site-faq-group-${gi}-down`} onClick={() => moveGroup(gi, 1)} className={iconBtn}><ArrowDown size={13} weight="bold" /></button>
            <button type="button" data-testid={`site-faq-group-${gi}-remove`} onClick={() => removeGroup(gi)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"><Trash size={13} weight="bold" /></button>
          </div>
          <div className="mt-3 space-y-3">
            {(g.items || []).map((it, ii) => (
              <div key={ii} className="rounded-md border border-zinc-800/70 bg-zinc-900/40 p-3" data-testid={`site-faq-${gi}-${ii}`}>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`site-faq-${gi}-${ii}-q`}
                    value={it.q || ""}
                    onChange={(e) => patchItem(gi, ii, "q", e.target.value)}
                    placeholder="Question"
                    className="h-9 border-zinc-800 bg-zinc-950 text-sm"
                  />
                  <button type="button" data-testid={`site-faq-${gi}-${ii}-up`} onClick={() => moveItem(gi, ii, -1)} className={iconBtn}><ArrowUp size={12} weight="bold" /></button>
                  <button type="button" data-testid={`site-faq-${gi}-${ii}-down`} onClick={() => moveItem(gi, ii, 1)} className={iconBtn}><ArrowDown size={12} weight="bold" /></button>
                  <button type="button" data-testid={`site-faq-${gi}-${ii}-remove`} onClick={() => removeItem(gi, ii)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"><Trash size={12} weight="bold" /></button>
                </div>
                <Textarea
                  data-testid={`site-faq-${gi}-${ii}-a`}
                  rows={2}
                  value={it.a || ""}
                  onChange={(e) => patchItem(gi, ii, "a", e.target.value)}
                  placeholder="Answer"
                  className="mt-2 border-zinc-800 bg-zinc-950 text-sm"
                />
              </div>
            ))}
            <Button type="button" data-testid={`site-faq-group-${gi}-add-item`} onClick={() => addItem(gi)} className="h-9 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
              <Plus size={13} weight="bold" className="mr-2" /> Add question
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" data-testid="site-faqs-add-group" onClick={addGroup} className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800">
        <Plus size={13} weight="bold" className="mr-2" /> Add group
      </Button>
    </div>
  );
};

const LinkRows = ({ items, onChange, testId, addLabel = "Add link" }) => {
  const patch = (i, key, value) =>
    onChange(items.map((x, n) => (n === i ? { ...x, [key]: value } : x)));
  const move = (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((item, i) => (
        <div
          key={`${testId}-${i}`}
          className="flex gap-2"
          data-testid={`${testId}-row-${i}`}
        >
          <Input
            data-testid={`${testId}-label-${i}`}
            value={item.label || ""}
            onChange={(e) => patch(i, "label", e.target.value)}
            placeholder="Label"
            className="h-10 border-zinc-800 bg-zinc-950 text-sm"
          />
          <Input
            data-testid={`${testId}-href-${i}`}
            value={item.href || ""}
            onChange={(e) => patch(i, "href", e.target.value)}
            placeholder="/inventory or https://…"
            className="h-10 border-zinc-800 bg-zinc-950 text-sm"
          />
          <button
            type="button"
            data-testid={`${testId}-up-${i}`}
            onClick={() => move(i, -1)}
            className="flex h-10 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
          >
            <ArrowUp size={13} weight="bold" />
          </button>
          <button
            type="button"
            data-testid={`${testId}-down-${i}`}
            onClick={() => move(i, 1)}
            className="flex h-10 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
          >
            <ArrowDown size={13} weight="bold" />
          </button>
          <button
            type="button"
            data-testid={`${testId}-remove-${i}`}
            onClick={() => onChange(items.filter((_, n) => n !== i))}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"
          >
            <Trash size={13} weight="bold" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        data-testid={`${testId}-add`}
        onClick={() => onChange([...items, { label: "", href: "" }])}
        className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
      >
        <Plus size={13} weight="bold" className="mr-2" /> {addLabel}
      </Button>
    </div>
  );
};

const ColumnsEditor = ({ columns, onChange, testId }) => {
  const patch = (i, next) =>
    onChange(columns.map((c, n) => (n === i ? next : c)));
  const move = (i, dir) => {
    const next = [...columns];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid={testId}>
      {columns.map((col, i) => (
        <div
          key={`${testId}-${i}`}
          className="rounded-md border border-zinc-800 bg-zinc-950 p-4"
          data-testid={`${testId}-col-${i}`}
        >
          <div className="flex items-center gap-2">
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-600">
              {i + 1}
            </span>
            <Input
              data-testid={`${testId}-heading-${i}`}
              value={col.heading || ""}
              onChange={(e) => patch(i, { ...col, heading: e.target.value })}
              placeholder="Column heading (e.g. Explore)"
              className="h-10 border-zinc-800 bg-zinc-900 text-sm"
            />
            <button
              type="button"
              data-testid={`${testId}-up-${i}`}
              onClick={() => move(i, -1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowUp size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`${testId}-down-${i}`}
              onClick={() => move(i, 1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowDown size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`${testId}-remove-${i}`}
              onClick={() => onChange(columns.filter((_, n) => n !== i))}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"
            >
              <Trash size={13} weight="bold" />
            </button>
          </div>
          <div className="mt-3">
            <LinkRows
              testId={`${testId}-links-${i}`}
              items={col.links || []}
              onChange={(links) => patch(i, { ...col, links })}
            />
          </div>
        </div>
      ))}
      <Button
        type="button"
        data-testid={`${testId}-add`}
        onClick={() =>
          onChange([...columns, { heading: "New column", links: [] }])
        }
        className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
      >
        <Plus size={13} weight="bold" className="mr-2" /> Add column
      </Button>
    </div>
  );
};

const NewsletterSubscribers = () => {
  const [subs, setSubs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api
      .get("/newsletter/subscribers")
      .then(({ data }) => {
        setSubs(data || []);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);
  const exportCsv = () => {
    const rows = [
      ["email", "name", "source", "created_at"],
      ...subs.map((s) => [s.email, s.name, s.source, s.created_at]),
    ];
    const csv = rows
      .map((r) =>
        r.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "newsletter-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="site-newsletter-subscribers">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-zinc-400">
          Subscribers ({subs.length})
        </div>
        {subs.length > 0 && (
          <Button
            type="button"
            data-testid="newsletter-export-csv"
            onClick={exportCsv}
            className="h-9 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
          >
            <DownloadSimple size={13} weight="bold" className="mr-2" /> Export CSV
          </Button>
        )}
      </div>
      <div className="mt-3 max-h-64 space-y-1 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 p-2">
        {!loaded && <p className="p-3 text-xs text-zinc-600">Loading…</p>}
        {loaded && subs.length === 0 && (
          <p className="p-3 text-xs text-zinc-600">No subscribers yet.</p>
        )}
        {subs.map((s, i) => (
          <div
            key={i}
            data-testid={`newsletter-sub-${i}`}
            className="flex items-center justify-between rounded px-3 py-2 text-sm hover:bg-zinc-900"
          >
            <span className="truncate text-zinc-200">{s.email}</span>
            <span className="font-mono-data ml-3 shrink-0 text-[11px] text-zinc-500">
              {(s.created_at || "").slice(0, 10)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatRows = ({ items, onChange, testId }) => {
  const patch = (i, key, value) =>
    onChange(items.map((x, n) => (n === i ? { ...x, [key]: value } : x)));
  return (
    <div className="space-y-2" data-testid={testId}>
      {items.map((item, i) => (
        <div
          key={`${testId}-${i}`}
          className="flex gap-2"
          data-testid={`${testId}-row-${i}`}
        >
          <Input
            data-testid={`${testId}-value-${i}`}
            value={item.value || ""}
            onChange={(e) => patch(i, "value", e.target.value)}
            placeholder="10,000+"
            className="h-10 w-28 shrink-0 border-zinc-800 bg-zinc-950 text-sm"
          />
          <Input
            data-testid={`${testId}-caption-${i}`}
            value={item.caption || ""}
            onChange={(e) => patch(i, "caption", e.target.value)}
            placeholder="Short caption under the number"
            className="h-10 border-zinc-800 bg-zinc-950 text-sm"
          />
          <button
            type="button"
            data-testid={`${testId}-remove-${i}`}
            onClick={() => onChange(items.filter((_, n) => n !== i))}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"
          >
            <Trash size={13} weight="bold" />
          </button>
        </div>
      ))}
      <Button
        type="button"
        data-testid={`${testId}-add`}
        onClick={() => onChange([...items, { value: "", caption: "" }])}
        className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
      >
        <Plus size={13} weight="bold" className="mr-2" /> Add stat
      </Button>
    </div>
  );
};

const TextList = ({ items, onChange, testId, placeholder }) => (
  <div className="space-y-2" data-testid={testId}>
    {items.map((value, i) => (
      <div key={`${testId}-${i}`} className="flex gap-2">
        <Input
          data-testid={`${testId}-${i}`}
          value={value}
          onChange={(e) =>
            onChange(items.map((x, n) => (n === i ? e.target.value : x)))
          }
          placeholder={placeholder}
          className="h-10 border-zinc-800 bg-zinc-950 text-sm"
        />
        <button
          type="button"
          data-testid={`${testId}-remove-${i}`}
          onClick={() => onChange(items.filter((_, n) => n !== i))}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"
        >
          <Trash size={13} weight="bold" />
        </button>
      </div>
    ))}
    <Button
      type="button"
      data-testid={`${testId}-add`}
      onClick={() => onChange([...items, ""])}
      className="h-10 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
    >
      <Plus size={13} weight="bold" className="mr-2" /> Add
    </Button>
  </div>
);

const OrderRows = ({ order, onChange }) => {
  const labels = Object.fromEntries(HOME_SECTIONS);
  const move = (i, dir) => {
    const next = [...order];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid="site-order-list">
      {order.map((key, i) => (
        <div
          key={key}
          data-testid={`site-order-${key}`}
          className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3"
        >
          <span className="font-mono-data text-[10px] tracking-widest text-zinc-600">
            {i + 1}
          </span>
          <span className="text-sm text-zinc-300">{labels[key] || key}</span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              data-testid={`site-order-up-${key}`}
              onClick={() => move(i, -1)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowUp size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`site-order-down-${key}`}
              onClick={() => move(i, 1)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowDown size={13} weight="bold" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const FeaturedPicker = ({ picks, onChange }) => {
  const [slabs, setSlabs] = useState([]);
  const [q, setQ] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  useEffect(() => {
    api
      .get("/slabs", { params: { status: "In Stock" } })
      .then(({ data }) => setSlabs(data || []))
      .catch(() => setSlabs([]));
  }, []);
  const byKey = {};
  slabs.forEach((s) => {
    byKey[`slab-${s.id}`] = s;
  });
  const add = (key) => {
    if (!picks.includes(key)) onChange([...picks, key]);
  };
  const remove = (key) => onChange(picks.filter((k) => k !== key));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= picks.length) return;
    const next = [...picks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...picks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const term = q.trim().toLowerCase();
  const results = slabs
    .filter((s) => !picks.includes(`slab-${s.id}`))
    .filter(
      (s) =>
        !term ||
        [s.slab_color, s.material, s.finish, s.supplier]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(term),
    )
    .slice(0, 8);
  return (
    <div className="space-y-3" data-testid="featured-picker">
      <div>
        <div className="mb-1.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
          <span>Chosen slabs ({picks.length})</span>
          {picks.length > 1 && (
            <span className="text-zinc-600">· drag to reorder</span>
          )}
        </div>
        {picks.length === 0 ? (
          <p className="text-xs text-zinc-600">
            None yet — search below and add the slabs you want to feature.
          </p>
        ) : (
          <div className="space-y-1.5">
            {picks.map((k, i) => {
              const s = byKey[k];
              return (
                <div
                  key={k}
                  data-testid={`featured-pick-${k}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    reorder(dragIdx, i);
                    setDragIdx(null);
                  }}
                  onDragEnd={() => setDragIdx(null)}
                  className={`flex items-center gap-2 rounded-lg border bg-zinc-950 px-3 py-2 transition-colors ${
                    dragIdx === i
                      ? "border-indigo-500/60 opacity-60"
                      : "border-zinc-800"
                  }`}
                >
                  <span
                    className="shrink-0 cursor-grab text-zinc-600 active:cursor-grabbing"
                    title="Drag to reorder"
                  >
                    <DotsSixVertical size={16} weight="bold" />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-200">
                    {s ? s.slab_color : k}
                    {s && (
                      <span className="ml-2 text-xs text-zinc-500">
                        {[s.material, s.finish].filter(Boolean).join(" · ")}
                      </span>
                    )}
                    {!s && (
                      <span className="ml-2 text-xs text-amber-400">
                        not in stock
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="text-zinc-500 hover:text-zinc-200 disabled:opacity-30"
                  >
                    <ArrowUp size={14} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === picks.length - 1}
                    className="text-zinc-500 hover:text-zinc-200 disabled:opacity-30"
                  >
                    <ArrowDown size={14} weight="bold" />
                  </button>
                  <button
                    type="button"
                    data-testid={`featured-pick-remove-${k}`}
                    onClick={() => remove(k)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div>
        <Input
          data-testid="featured-pick-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search in-stock slabs to add…"
          className="h-11 border-zinc-800 bg-zinc-950"
        />
        {results.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {results.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`featured-add-${s.id}`}
                onClick={() => add(`slab-${s.id}`)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-left transition-colors hover:bg-zinc-800"
              >
                <span className="min-w-0 truncate text-sm text-zinc-200">
                  {s.slab_color}
                  <span className="ml-2 text-xs text-zinc-500">
                    {[s.material, s.finish].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className="shrink-0 text-xs font-semibold text-indigo-400">
                  Add
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const blankBrand = () => ({
  slug: "",
  name: "",
  eyebrow: "Collections · Brand",
  tagline: "",
  description: "",
  hero_image: "",
  materials: [],
  features: [],
  gallery: [],
  cta_label: "Request a quote",
  cta_url: "/contact",
  cta_external: false,
  cta_heading: "",
  cta_body: "",
  seo_title: "",
  seo_description: "",
  enabled: true,
});

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const BrandsEditor = ({ brands, onChange }) => {
  const list = brands || [];
  const [openIdx, setOpenIdx] = useState(null);
  const patch = (i, key, value) =>
    onChange(list.map((b, n) => (n === i ? { ...b, [key]: value } : b)));
  const patchName = (i, value) =>
    onChange(
      list.map((b, n) =>
        n === i ? { ...b, name: value, slug: b.slug || slugify(value) } : b,
      ),
    );
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, n) => n !== i));
  const add = () => {
    onChange([...list, blankBrand()]);
    setOpenIdx(list.length);
  };

  return (
    <div className="space-y-3" data-testid="site-brands-editor">
      {list.map((b, i) => (
        <div
          key={i}
          data-testid={`site-brand-row-${i}`}
          className="rounded-md border border-zinc-800 bg-zinc-950"
        >
          <div className="flex items-center gap-2 p-4">
            <button
              type="button"
              data-testid={`site-brand-row-${i}-toggle`}
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
              <span className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-600">
                {i + 1}
              </span>
              <span className="truncate text-sm font-semibold text-zinc-200">
                {b.name || "Untitled brand"}
              </span>
              {b.enabled === false && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500">
                  hidden
                </span>
              )}
              <CaretDown
                size={13}
                weight="bold"
                className={`ml-auto shrink-0 text-zinc-500 transition-transform ${openIdx === i ? "rotate-180" : ""}`}
              />
            </button>
            <button
              type="button"
              data-testid={`site-brand-up-${i}`}
              onClick={() => move(i, -1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowUp size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`site-brand-down-${i}`}
              onClick={() => move(i, 1)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100"
            >
              <ArrowDown size={13} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`site-brand-remove-${i}`}
              onClick={() => remove(i)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"
            >
              <Trash size={13} weight="bold" />
            </button>
          </div>
          {openIdx === i && (
            <div className="space-y-4 border-t border-zinc-800 p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Brand name">
                  <Input
                    data-testid={`site-brand-name-${i}`}
                    value={b.name || ""}
                    onChange={(e) => patchName(i, e.target.value)}
                    placeholder="Neolith"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
                <Field label="URL slug" hint="Page lives at /collections/brands/slug">
                  <Input
                    data-testid={`site-brand-slug-${i}`}
                    value={b.slug || ""}
                    onChange={(e) => patch(i, "slug", slugify(e.target.value))}
                    placeholder="neolith"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
              </div>
              <Field label="Eyebrow (small line above the name)">
                <Input
                  data-testid={`site-brand-eyebrow-${i}`}
                  value={b.eyebrow || ""}
                  onChange={(e) => patch(i, "eyebrow", e.target.value)}
                  placeholder="Collections · Brand"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field
                label="Tagline"
                hint="One line under the name (also shows in the Collections menu)"
              >
                <Input
                  data-testid={`site-brand-tagline-${i}`}
                  value={b.tagline || ""}
                  onChange={(e) => patch(i, "tagline", e.target.value)}
                  placeholder="Sintered stone — the surface that does it all"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Description">
                <Textarea
                  data-testid={`site-brand-description-${i}`}
                  rows={4}
                  value={b.description || ""}
                  onChange={(e) => patch(i, "description", e.target.value)}
                  className="mt-1 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <ImageField
                label="Hero background image"
                testId={`site-brand-hero-${i}`}
                value={b.hero_image}
                onChange={(v) => patch(i, "hero_image", v)}
              />
              <Field
                label="Uses / materials"
                hint="Comma-separated chips (e.g. Kitchen countertops, Bathrooms, Flooring)"
              >
                <Input
                  data-testid={`site-brand-materials-${i}`}
                  value={(b.materials || []).join(", ")}
                  onChange={(e) =>
                    patch(
                      i,
                      "materials",
                      e.target.value
                        .split(",")
                        .map((x) => x.trim())
                        .filter(Boolean),
                    )
                  }
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field
                label="Feature cards"
                hint="A title and one line each — the 'Why this brand' grid"
              >
                <CardRows
                  testId={`site-brand-features-${i}`}
                  items={b.features || []}
                  fields={["title", "body"]}
                  onChange={(v) => patch(i, "features", v)}
                />
              </Field>
              <Field
                label="Collection cards"
                hint="Each: picture, title, a small tag, one line, and an optional link"
              >
                <CardRows
                  testId={`site-brand-gallery-${i}`}
                  items={b.gallery || []}
                  fields={["title", "tag", "body", "image", "href"]}
                  onChange={(v) => patch(i, "gallery", v)}
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Main button label">
                  <Input
                    data-testid={`site-brand-cta-label-${i}`}
                    value={b.cta_label || ""}
                    onChange={(e) => patch(i, "cta_label", e.target.value)}
                    placeholder="See Neolith in stock"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
                <Field label="Main button link">
                  <Input
                    data-testid={`site-brand-cta-url-${i}`}
                    value={b.cta_url || ""}
                    onChange={(e) => patch(i, "cta_url", e.target.value)}
                    placeholder="/inventory?brand=Neolith"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
              </div>
              <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-900 px-4 py-3">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                  Button opens an external site (new tab)
                </span>
                <Switch
                  data-testid={`site-brand-cta-external-${i}`}
                  checked={!!b.cta_external}
                  onCheckedChange={(v) => patch(i, "cta_external", v)}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Closing heading">
                  <Input
                    data-testid={`site-brand-cta-heading-${i}`}
                    value={b.cta_heading || ""}
                    onChange={(e) => patch(i, "cta_heading", e.target.value)}
                    placeholder="See Neolith in person"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
                <Field label="Closing body">
                  <Textarea
                    data-testid={`site-brand-cta-body-${i}`}
                    rows={2}
                    value={b.cta_body || ""}
                    onChange={(e) => patch(i, "cta_body", e.target.value)}
                    className="mt-1 border-zinc-800 bg-zinc-950"
                  />
                </Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="SEO title">
                  <Input
                    data-testid={`site-brand-seo-title-${i}`}
                    value={b.seo_title || ""}
                    onChange={(e) => patch(i, "seo_title", e.target.value)}
                    className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
                <Field label="SEO description">
                  <Textarea
                    data-testid={`site-brand-seo-desc-${i}`}
                    rows={2}
                    value={b.seo_description || ""}
                    onChange={(e) => patch(i, "seo_description", e.target.value)}
                    className="mt-1 border-zinc-800 bg-zinc-950"
                  />
                </Field>
              </div>
              <div className="flex items-center justify-between border-t border-zinc-800 pt-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Show this brand under Collections
                </span>
                <Switch
                  data-testid={`site-brand-enabled-${i}`}
                  checked={b.enabled !== false}
                  onCheckedChange={(v) => patch(i, "enabled", v)}
                />
              </div>
            </div>
          )}
        </div>
      ))}
      <Button
        type="button"
        data-testid="site-brand-add"
        onClick={add}
        className="h-11 w-full border border-dashed border-zinc-700 bg-transparent text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
      >
        <Plus size={14} weight="bold" className="mr-2" /> Add brand
      </Button>
    </div>
  );
};

export default function SiteSettings() {
  const [cfg, setCfg] = useState(null);
  const [stripProjects, setStripProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [open, setOpen] = useState("general");
  const selectTab = (id) => {
    setActiveTab(id);
    setOpen(TAB_DEFS.find((t) => t.id === id)?.first || "");
  };
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const results = q
    ? SECTION_META.filter((m) =>
        `${m.title} ${m.kw || ""} ${m.tab}`.toLowerCase().includes(q),
      ).slice(0, 8)
    : [];
  const goToSection = (m) => {
    setActiveTab(m.tab);
    setOpen(m.id);
    setQuery("");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${m.testId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-indigo-500/70");
        setTimeout(
          () => el.classList.remove("ring-2", "ring-indigo-500/70"),
          1600,
        );
      }
    }, 70);
  };

  useEffect(() => {
    api
      .get("/settings/site")
      .then(({ data }) => setCfg((c) => c ?? data))
      .catch(() => toast.error("Could not load the website settings"));
    api
      .get("/settings/site/google-reviews")
      .then(({ data }) => setGoogleReviews(data))
      .catch(() => {});
    api
      .get("/public/gallery")
      .then(({ data }) => setStripProjects(data.items || []))
      .catch(() => {});
  }, []);

  const [googleReviews, setGoogleReviews] = useState(null);
  const [syncingGoogle, setSyncingGoogle] = useState(false);

  const syncGoogleReviews = async () => {
    setSyncingGoogle(true);
    try {
      // save first so a freshly pasted key / Place ID is on the server
      const { data: saved } = await api.put("/settings/site", cfg);
      setCfg(saved);
      const { data } = await api.post("/settings/site/google-reviews/sync");
      setGoogleReviews(data);
      if (data.error) toast.error(data.error);
      else toast.success(`Pulled ${data.reviews} Google review${data.reviews === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not sync Google reviews",
      );
    }
    setSyncingGoogle(false);
  };

  const removeGoogleKey = async () => {
    try {
      const { data } = await api.delete("/settings/site/google-reviews/key");
      setGoogleReviews(data);
      set("google", "places_api_key_set", false);
      toast.success("Places API key removed");
    } catch {
      toast.error("Could not remove the key");
    }
  };

  const set = useCallback(
    (block, key, value) =>
      setCfg((c) => ({ ...c, [block]: { ...c[block], [key]: value } })),
    [],
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/site", cfg);
      setCfg(data);
      toast.success("Website updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
    setSaving(false);
  };

  if (!cfg)
    return (
      <div className="min-h-screen">
        <AppHeader title="Public Website" />
        <div className="mx-auto max-w-4xl px-4 py-10">
          <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
        </div>
      </div>
    );

  const panel = (id) => ({
    open: open === id,
    onToggle: () => setOpen(open === id ? "" : id),
    hidden: SECTION_TABS[id] !== activeTab,
  });

  return (
    <div className="min-h-screen">
      <AppHeader title="Public Website" />
      <main className="mx-auto max-w-6xl px-4 py-8" data-testid="site-settings">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Public website
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Everything visitors see on the front site — copy, pictures, which
              sections show, socials, SEO and analytics.
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="site-settings-view"
            className="ml-auto flex h-11 items-center gap-2 rounded-lg border border-zinc-800 px-4 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
          >
            <Globe size={14} weight="bold" /> View site
          </a>
          <Button
            data-testid="site-settings-save"
            disabled={saving}
            onClick={save}
            className="h-11 bg-indigo-500 px-6 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
          >
            <FloppyDisk size={14} weight="bold" className="mr-2" />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <div className="relative mt-6 max-w-md">
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <Input
            data-testid="site-settings-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results[0]) goToSection(results[0]);
              if (e.key === "Escape") setQuery("");
            }}
            onBlur={() => setTimeout(() => setQuery(""), 150)}
            placeholder="Jump to a setting — hero, footer, SEO, contact…"
            className="h-11 border-zinc-800 bg-zinc-900 pl-10 text-sm"
          />
          {query && (
            <div
              data-testid="site-search-results"
              className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 shadow-xl"
            >
              {results.length === 0 && (
                <p className="px-4 py-3 text-xs text-zinc-500">
                  No setting matches “{query}”.
                </p>
              )}
              {results.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`site-search-result-${m.id}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    goToSection(m);
                  }}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-zinc-800"
                >
                  <span className="text-sm text-zinc-200">{m.title}</span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    {TAB_DEFS.find((t) => t.id === m.tab)?.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          <nav
            data-testid="site-settings-nav"
            className="flex gap-2 overflow-x-auto pb-1 lg:sticky lg:top-8 lg:w-52 lg:shrink-0 lg:flex-col lg:overflow-visible lg:pb-0"
          >
            {TAB_DEFS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                data-testid={`site-tab-${id}`}
                onClick={() => selectTab(id)}
                className={`flex shrink-0 items-center gap-2 rounded-lg border px-4 py-3 text-[11px] font-bold uppercase tracking-widest transition-colors ${
                  activeTab === id
                    ? "border-indigo-500/60 bg-indigo-500/10 text-indigo-300"
                    : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:bg-zinc-800/60"
                }`}
              >
                <Icon size={16} weight="bold" />
                {label}
              </button>
            ))}
          </nav>

          <div className="min-w-0 flex-1 space-y-3">
          <Section
            title="Site live"
            sub="Turn the whole public site off if you are not ready to show it"
            testId="site-general"
            right={
              <Toggle
                id="enabled"
                label="Live"
                value={cfg.enabled}
                onChange={(v) => setCfg({ ...cfg, enabled: v })}
              />
            }
            {...panel("general")}
          >
            <p className="text-xs text-zinc-500">
              When this is off, visitors to / see a short &ldquo;coming
              soon&rdquo; card with your phone number. The customer portal and
              the CRM keep working.
            </p>
          </Section>

          <Section
            title="Header logo"
            sub="Upload the logo for both header states — see-through over the hero, and solid once the page is scrolled"
            testId="site-branding"
            {...panel("branding")}
          >
            <p className="text-xs text-zinc-500">
              Leave a slot empty and the site falls back to your shop logo and
              name. PNG or SVG with a transparent background works best.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <ImageField
                label="Regular header — light logo (over the hero photo)"
                testId="site-logo-regular-light"
                contain
                value={cfg.branding.logo_regular_light}
                onChange={(v) => set("branding", "logo_regular_light", v)}
              />
              <ImageField
                label="Regular header — dark logo"
                testId="site-logo-regular-dark"
                contain
                value={cfg.branding.logo_regular_dark}
                onChange={(v) => set("branding", "logo_regular_dark", v)}
              />
              <ImageField
                label="Sticky header — dark logo (white bar, scrolled)"
                testId="site-logo-sticky-dark"
                contain
                value={cfg.branding.logo_sticky_dark}
                onChange={(v) => set("branding", "logo_sticky_dark", v)}
              />
              <ImageField
                label="Sticky header — light logo"
                testId="site-logo-sticky-light"
                contain
                value={cfg.branding.logo_sticky_light}
                onChange={(v) => set("branding", "logo_sticky_light", v)}
              />
            </div>
          </Section>

          <Section
            title="Home page order"
            sub="Move the blocks up or down to change the order of the home page"
            testId="site-order"
            {...panel("order")}
          >
            <OrderRows
              order={sectionOrder(cfg)}
              onChange={(v) => setCfg({ ...cfg, order: v })}
            />
          </Section>

          <Section
            title="Hero"
            sub="The first screen: headline, blurb, buttons and background photo"
            testId="site-hero"
            {...panel("hero")}
          >
            <Field label="Small line above the headline">
              <Input
                data-testid="site-hero-eyebrow"
                value={cfg.hero.eyebrow}
                onChange={(e) => set("hero", "eyebrow", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Headline — first line">
                <Input
                  data-testid="site-hero-title-top"
                  value={cfg.hero.title_top}
                  onChange={(e) => set("hero", "title_top", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Headline — gold line">
                <Input
                  data-testid="site-hero-title-accent"
                  value={cfg.hero.title_accent}
                  onChange={(e) => set("hero", "title_accent", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Headline size">              <div className="mt-2 flex items-center gap-4">
                <input
                  type="range"
                  min="50"
                  max="160"
                  step="5"
                  data-testid="site-hero-title-scale"
                  value={cfg.hero.title_scale ?? 100}
                  onChange={(e) =>
                    set("hero", "title_scale", Number(e.target.value))
                  }
                  className="h-1 flex-1 cursor-pointer accent-indigo-400"
                />
                <span
                  className="w-14 text-right text-sm text-zinc-400"
                  data-testid="site-hero-title-scale-value"
                >
                  {cfg.hero.title_scale ?? 100}%
                </span>
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                Scales the big hero headline on every screen size.
              </p>
            </Field>
            <Field label="Blurb">
              <Textarea
                data-testid="site-hero-body"
                rows={3}
                value={cfg.hero.body}
                onChange={(e) => set("hero", "body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Main button">
                <Input
                  data-testid="site-hero-primary"
                  value={cfg.hero.primary_label}
                  onChange={(e) => set("hero", "primary_label", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Second button">
                <Input
                  data-testid="site-hero-secondary"
                  value={cfg.hero.secondary_label}
                  onChange={(e) =>
                    set("hero", "secondary_label", e.target.value)
                  }
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <ImageField
              label="Background photo"
              testId="site-hero-image"
              value={cfg.hero.image}
              onChange={(v) => set("hero", "image", v)}
            />
            <Field
              label="Background video (optional)"
              hint="YouTube link or a direct .mp4 URL — plays muted behind the headline; the photo stays as the fallback"
            >
              <Input
                data-testid="site-hero-video"
                value={cfg.hero.video_url || ""}
                onChange={(e) => set("hero", "video_url", e.target.value)}
                placeholder="https://www.youtube.com/watch?v=… or https://…/shop.mp4"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <ImageField
                label="Corner preview photo"
                testId="site-hero-tour-image"
                value={cfg.hero.tour_image}
                onChange={(v) => set("hero", "tour_image", v)}
              />
              <Field label="Corner preview video link (optional)">
                <Input
                  data-testid="site-hero-tour-url"
                  placeholder="https://youtu.be/…"
                  value={cfg.hero.tour_url}
                  onChange={(e) => set("hero", "tour_url", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
                <p className="mt-2 text-xs text-zinc-500">
                  Leave empty and the card opens your gallery instead.
                </p>
              </Field>
            </div>
          </Section>

          <Section
            title="Numbers band"
            sub="Live stock counts and your review score on the glass panel"
            testId="site-stats"
            right={
              <Toggle
                id="stats"
                value={cfg.stats.enabled}
                onChange={(v) => set("stats", "enabled", v)}
              />
            }
            {...panel("stats")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Review score">
                <Input
                  data-testid="site-stats-rating"
                  value={cfg.stats.rating}
                  onChange={(e) => set("stats", "rating", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Number of reviews">
                <Input
                  data-testid="site-stats-reviews"
                  value={cfg.stats.reviews}
                  onChange={(e) => set("stats", "reviews", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["show_slabs", "Slabs in stock"],
                ["show_colors", "Colors"],
                ["show_remnants", "Remnants"],
                ["show_rating", "Review score"],
                ["show_one_shop", "One shop, start to finish"],
              ].map(([key, label]) => (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3"
                >
                  <span className="text-xs text-zinc-400">{label}</span>
                  <Switch
                    data-testid={`site-stats-${key}`}
                    checked={!!cfg.stats[key]}
                    onCheckedChange={(v) => set("stats", key, v)}
                  />
                </div>
              ))}
            </div>
            <ImageField
              label="Band photo"
              testId="site-stats-image"
              value={cfg.stats.image}
              onChange={(v) => set("stats", "image", v)}
            />
          </Section>

          <Section
            title="About / founder block"
            sub="Founder photo beside your story, a pull-quote and a Read more button (home page + About page)"
            testId="site-founder-quote-editor"
            right={
              <Toggle
                id="founder_quote"
                value={cfg.founder_quote.enabled}
                onChange={(v) => set("founder_quote", "enabled", v)}
              />
            }
            {...panel("founder_quote")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Small line" hint="Eyebrow above the heading, e.g. About">
                <Input
                  data-testid="site-founder-micro"
                  value={cfg.founder_quote.micro}
                  onChange={(e) => set("founder_quote", "micro", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Button label">
                <Input
                  data-testid="site-founder-cta-label"
                  value={cfg.founder_quote.cta_label || ""}
                  onChange={(e) => set("founder_quote", "cta_label", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Heading">
              <Input
                data-testid="site-founder-heading"
                value={cfg.founder_quote.heading || ""}
                onChange={(e) => set("founder_quote", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Story paragraph">
              <Textarea
                data-testid="site-founder-body"
                rows={4}
                value={cfg.founder_quote.body || ""}
                onChange={(e) => set("founder_quote", "body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Pull-quote" hint="Leave blank to hide the quote">
              <Textarea
                data-testid="site-founder-quote-text"
                rows={3}
                value={cfg.founder_quote.quote}
                onChange={(e) => set("founder_quote", "quote", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Author name">
                <Input
                  data-testid="site-founder-author"
                  value={cfg.founder_quote.author}
                  onChange={(e) => set("founder_quote", "author", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Author role">
                <Input
                  data-testid="site-founder-role"
                  value={cfg.founder_quote.role}
                  onChange={(e) => set("founder_quote", "role", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <ImageField
                label="Founder photo"
                testId="site-founder-image"
                value={cfg.founder_quote.image}
                onChange={(v) => set("founder_quote", "image", v)}
              />
              <ImageField
                label="Second photo (offset frame)"
                testId="site-founder-image2"
                value={cfg.founder_quote.image2 || ""}
                onChange={(v) => set("founder_quote", "image2", v)}
              />
            </div>
          </Section>

          <Section
            title="Our advantages"
            sub="A tap-through list beside a feature image"
            testId="site-advantages-editor"
            right={
              <Toggle
                id="advantages"
                value={cfg.advantages.enabled}
                onChange={(v) => set("advantages", "enabled", v)}
              />
            }
            {...panel("advantages")}
          >
            <Field label="Small line">
              <Input
                data-testid="site-advantages-micro"
                value={cfg.advantages.micro}
                onChange={(e) => set("advantages", "micro", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading over the image">
              <Input
                data-testid="site-advantages-heading"
                value={cfg.advantages.heading}
                onChange={(e) => set("advantages", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Feature image"
              testId="site-advantages-image"
              value={cfg.advantages.image}
              onChange={(v) => set("advantages", "image", v)}
            />
            <CardRows
              testId="site-advantages-items"
              items={cfg.advantages.items}
              fields={["title", "body"]}
              onChange={(v) => set("advantages", "items", v)}
            />
          </Section>

          <Section
            title="Comfort & space"
            sub="Numbered columns with vertical labels"
            testId="site-innovation-editor"
            right={
              <Toggle
                id="innovation"
                value={cfg.innovation.enabled}
                onChange={(v) => set("innovation", "enabled", v)}
              />
            }
            {...panel("innovation")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Small line">
                <Input
                  data-testid="site-innovation-micro"
                  value={cfg.innovation.micro}
                  onChange={(e) => set("innovation", "micro", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Heading">
                <Input
                  data-testid="site-innovation-heading"
                  value={cfg.innovation.heading}
                  onChange={(e) => set("innovation", "heading", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Lead paragraph">
              <Textarea
                data-testid="site-innovation-lead"
                rows={2}
                value={cfg.innovation.lead_body}
                onChange={(e) => set("innovation", "lead_body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Feature image"
              testId="site-innovation-image"
              value={cfg.innovation.image}
              onChange={(v) => set("innovation", "image", v)}
            />
            <CardRows
              testId="site-innovation-items"
              items={cfg.innovation.items}
              fields={["title"]}
              onChange={(v) => set("innovation", "items", v)}
            />
          </Section>

          <Section
            title="Our projects"
            sub="A heading and a row of project thumbnails"
            testId="site-projects-editor"
            right={
              <Toggle
                id="projects"
                value={cfg.projects.enabled}
                onChange={(v) => set("projects", "enabled", v)}
              />
            }
            {...panel("projects")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Small line">
                <Input
                  data-testid="site-projects-micro"
                  value={cfg.projects.micro}
                  onChange={(e) => set("projects", "micro", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Heading">
                <Input
                  data-testid="site-projects-heading"
                  value={cfg.projects.heading}
                  onChange={(e) => set("projects", "heading", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Side copy">
              <Textarea
                data-testid="site-projects-sub"
                rows={2}
                value={cfg.projects.sub}
                onChange={(e) => set("projects", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <CardRows
              testId="site-projects-items"
              items={cfg.projects.items}
              fields={["title", "body", "image"]}
              onChange={(v) => set("projects", "items", v)}
            />
          </Section>

          <Section
            title="Let's talk"
            sub="Oversized closing call-to-action with an image strip"
            testId="site-lets-talk-editor"
            right={
              <Toggle
                id="lets_talk"
                value={cfg.lets_talk.enabled}
                onChange={(v) => set("lets_talk", "enabled", v)}
              />
            }
            {...panel("lets_talk")}
          >
            <Field label="Big heading">
              <Input
                data-testid="site-lets-talk-heading"
                value={cfg.lets_talk.heading}
                onChange={(e) => set("lets_talk", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Sub copy">
              <Textarea
                data-testid="site-lets-talk-body"
                rows={2}
                value={cfg.lets_talk.body}
                onChange={(e) => set("lets_talk", "body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </Section>

          <Section
            title="Material categories"
            sub="The four stone tiles that link into your inventory"
            testId="site-materials"
            right={
              <Toggle
                id="materials"
                value={cfg.materials.enabled}
                onChange={(v) => set("materials", "enabled", v)}
              />
            }
            {...panel("materials")}
          >
            <Field label="Small line">
              <Input
                data-testid="site-materials-micro"
                value={cfg.materials.micro}
                onChange={(e) => set("materials", "micro", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading">
              <Input
                data-testid="site-materials-heading"
                value={cfg.materials.heading}
                onChange={(e) => set("materials", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Side copy">
              <Textarea
                data-testid="site-materials-sub"
                rows={2}
                value={cfg.materials.sub}
                onChange={(e) => set("materials", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <CardRows
              testId="site-materials-items"
              items={cfg.materials.items}
              fields={["title", "body", "category", "image"]}
              onChange={(v) => set("materials", "items", v)}
            />
          </Section>

          <Section
            title="Appearance"
            sub="Homepage background theme, custom color and section spacing"
            testId="site-appearance"
            {...panel("appearance")}
          >
            <Field label="Background theme">
              <div className="mt-1 flex gap-2" data-testid="site-appearance-theme">
                {["light", "dark"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`site-appearance-theme-${v}`}
                    onClick={() => set("appearance", "theme", v)}
                    className={`h-11 flex-1 border text-sm capitalize transition-colors ${
                      (cfg.appearance?.theme || "light") === v
                        ? "border-[color:var(--s-gold)] bg-zinc-800 text-white"
                        : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Custom background color" hint="Optional hex (e.g. #14140f). Leave blank to use the theme default.">
              <div className="mt-1 flex items-center gap-3">
                <input
                  type="color"
                  value={cfg.appearance?.bg || (cfg.appearance?.theme === "dark" ? "#17171a" : "#fbfbf9")}
                  onChange={(e) => set("appearance", "bg", e.target.value)}
                  data-testid="site-appearance-bg-picker"
                  className="h-11 w-14 shrink-0 cursor-pointer border border-zinc-800 bg-transparent"
                />
                <Input
                  value={cfg.appearance?.bg || ""}
                  onChange={(e) => set("appearance", "bg", e.target.value)}
                  placeholder="#17171a"
                  data-testid="site-appearance-bg"
                  className="h-11 flex-1 border-zinc-800 bg-zinc-950"
                />
                {cfg.appearance?.bg ? (
                  <button
                    type="button"
                    onClick={() => set("appearance", "bg", "")}
                    data-testid="site-appearance-bg-clear"
                    className="shrink-0 text-xs text-zinc-400 underline hover:text-white"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </Field>
            <Field label="Section spacing" hint="Vertical padding / margin between homepage sections">
              <div className="mt-1 flex gap-2" data-testid="site-appearance-space">
                {["compact", "normal", "roomy"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`site-appearance-space-${v}`}
                    onClick={() => set("appearance", "space", v)}
                    className={`h-11 flex-1 border text-sm capitalize transition-colors ${
                      (cfg.appearance?.space || "normal") === v
                        ? "border-[color:var(--s-gold)] bg-zinc-800 text-white"
                        : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Section width" hint="Full-bleed edge-to-edge, or aligned to the 1720px content column">
              <div className="mt-1 space-y-1.5" data-testid="site-appearance-widths">
                {HOME_SECTIONS.map(([id, label]) => {
                  const w = cfg.appearance?.widths?.[id] || "content";
                  return (
                    <div key={id} className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-950 px-3 py-2">
                      <span className="truncate text-xs text-zinc-300">{label}</span>
                      <div className="flex shrink-0 gap-1">
                        {["content", "full"].map((v) => (
                          <button
                            key={v}
                            type="button"
                            data-testid={`site-appearance-width-${id}-${v}`}
                            onClick={() => set("appearance", "widths", { ...(cfg.appearance?.widths || {}), [id]: v })}
                            className={`h-8 px-3 text-[11px] capitalize transition-colors ${
                              w === v ? "bg-[color:var(--s-gold)] text-black" : "bg-zinc-800 text-zinc-400 hover:text-white"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Field>
          </Section>

          <Section
            title="How we work"
            sub="The numbered process stepper — steps, per-step photos and the copy beside them"
            testId="site-workflow"
            right={
              <Toggle
                id="workflow"
                value={cfg.workflow.enabled}
                onChange={(v) => set("workflow", "enabled", v)}
              />
            }
            {...panel("workflow")}
          >
            <Field label="Eyebrow">
              <Input
                data-testid="site-workflow-micro"
                value={cfg.workflow.micro || ""}
                onChange={(e) => set("workflow", "micro", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading">
              <Input
                data-testid="site-workflow-heading"
                value={cfg.workflow.heading}
                onChange={(e) => set("workflow", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Side copy">
              <Textarea
                data-testid="site-workflow-sub"
                rows={2}
                value={cfg.workflow.sub}
                onChange={(e) => set("workflow", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Default step photo"
              testId="site-workflow-image"
              value={cfg.workflow.image}
              onChange={(v) => set("workflow", "image", v)}
            />
            <Field
              label="Steps"
              hint="Up to 5 steps show as the 01–05 selector. Each has a title, a line of copy and its own photo (falls back to the default photo above)."
            >
              <CardRows
                testId="site-workflow-items"
                items={cfg.workflow.items}
                fields={["title", "body", "image"]}
                onChange={(v) => set("workflow", "items", v)}
              />
            </Field>
          </Section>

          <Section
            title="Designs reflect you"
            sub="Custom guarantees — 2×2 icon cards + a photo with a checklist overlay"
            testId="site-reflect"
            right={
              <Toggle
                id="reflect"
                value={cfg.reflect?.enabled !== false}
                onChange={(v) => set("reflect", "enabled", v)}
              />
            }
            {...panel("reflect")}
          >
            <Field label="Eyebrow">
              <Input
                data-testid="site-reflect-micro"
                value={cfg.reflect?.micro || ""}
                onChange={(e) => set("reflect", "micro", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading">
              <Input
                data-testid="site-reflect-heading"
                value={cfg.reflect?.heading || ""}
                onChange={(e) => set("reflect", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Sub copy">
              <Textarea
                data-testid="site-reflect-sub"
                rows={2}
                value={cfg.reflect?.sub || ""}
                onChange={(e) => set("reflect", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Large photo (right side)"
              testId="site-reflect-image"
              value={cfg.reflect?.image}
              onChange={(v) => set("reflect", "image", v)}
            />
            <Field label="Icon cards" hint="Four cards, each with a title and one line of copy">
              <CardRows
                testId="site-reflect-items"
                items={cfg.reflect?.items || []}
                onChange={(v) => set("reflect", "items", v)}
              />
            </Field>
            <Field label="Checklist" hint="Short items shown over the photo (up to 6)">
              <TextList
                testId="site-reflect-checklist"
                items={cfg.reflect?.checklist || []}
                onChange={(v) => set("reflect", "checklist", v)}
                placeholder="e.g. Vein matching"
              />
            </Field>
          </Section>

          <Section
            title="Featured slabs"
            sub="Shown on the home page as “New arrivals & deals”"
            testId="site-featured"
            right={
              <Toggle
                id="featured"
                value={cfg.featured.enabled}
                onChange={(v) => set("featured", "enabled", v)}
              />
            }
            {...panel("featured")}
          >
            <Field label="Heading">
              <Input
                data-testid="site-featured-heading"
                value={cfg.featured.heading}
                onChange={(e) => set("featured", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Which slabs to show">
              <div className="mt-1 flex gap-1 rounded-lg border border-zinc-800 bg-zinc-950 p-1">
                {[
                  ["auto", "Auto (New / On sale)"],
                  ["manual", "Hand-pick"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`featured-mode-${k}`}
                    onClick={() => set("featured", "mode", k)}
                    className={`flex-1 rounded-md px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                      (cfg.featured.mode || "auto") === k
                        ? "bg-zinc-800 text-zinc-100"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>
            {(cfg.featured.mode || "auto") === "manual" && (
              <FeaturedPicker
                picks={cfg.featured.picks || []}
                onChange={(v) => set("featured", "picks", v)}
              />
            )}
            <Field label="How many to show" hint="Between 4 and 12 looks best">
              <Input
                data-testid="site-featured-limit"
                type="number"
                min={1}
                max={24}
                value={cfg.featured.limit}
                onChange={(e) =>
                  set("featured", "limit", Number(e.target.value))
                }
                className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </Section>

          <Section
            title="3D kitchen tour"
            sub="A tour embed (YouTube / Matterport / video) or an image slider — featuring Taj Mahal quartzite"
            testId="site-kitchen-tour"
            right={
              <Toggle
                id="kitchen_tour"
                value={cfg.kitchen_tour.enabled}
                onChange={(v) => set("kitchen_tour", "enabled", v)}
              />
            }
            {...panel("kitchen_tour")}
          >
            <Field label="Small line">
              <Input
                data-testid="site-kitchen-tour-micro"
                value={cfg.kitchen_tour.micro}
                onChange={(e) => set("kitchen_tour", "micro", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading">
              <Input
                data-testid="site-kitchen-tour-heading"
                value={cfg.kitchen_tour.heading}
                onChange={(e) => set("kitchen_tour", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Sub copy">
              <Textarea
                data-testid="site-kitchen-tour-sub"
                rows={2}
                value={cfg.kitchen_tour.sub}
                onChange={(e) => set("kitchen_tour", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="3D tour link (optional)"
              hint="Paste a YouTube, Matterport or video (.mp4) link to embed a real tour. Leave blank to show the image slider below."
            >
              <Input
                data-testid="site-kitchen-tour-embed"
                value={cfg.kitchen_tour.embed_url}
                onChange={(e) => set("kitchen_tour", "embed_url", e.target.value)}
                placeholder="https://my.matterport.com/show/?m=…  or  https://youtu.be/…"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Slider images" hint="Shown when there is no tour link. The title becomes the caption.">
              <CardRows
                testId="site-kitchen-tour-slides"
                items={cfg.kitchen_tour.slides || []}
                fields={["title", "image"]}
                onChange={(v) => set("kitchen_tour", "slides", v)}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Button label">
                <Input
                  data-testid="site-kitchen-tour-cta-label"
                  value={cfg.kitchen_tour.cta_label}
                  onChange={(e) => set("kitchen_tour", "cta_label", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Button link" hint="e.g. /inventory?q=Taj Mahal">
                <Input
                  data-testid="site-kitchen-tour-cta-href"
                  value={cfg.kitchen_tour.cta_href}
                  onChange={(e) => set("kitchen_tour", "cta_href", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Link to a slab (optional)" hint="A slab key like slab-… overrides the button link to that product page.">
              <Input
                data-testid="site-kitchen-tour-slab"
                value={cfg.kitchen_tour.slab_key}
                onChange={(e) => set("kitchen_tour", "slab_key", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </Section>

          <Section
            title="Why us"
            sub="Six selling points either side of a photo"
            testId="site-why"
            right={
              <Toggle
                id="why"
                value={cfg.why.enabled}
                onChange={(v) => set("why", "enabled", v)}
              />
            }
            {...panel("why")}
          >
            <Field label="Heading">
              <Input
                data-testid="site-why-heading"
                value={cfg.why.heading}
                onChange={(e) => set("why", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Sub copy">
              <Textarea
                data-testid="site-why-sub"
                rows={2}
                value={cfg.why.sub}
                onChange={(e) => set("why", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Middle photo"
              testId="site-why-image"
              value={cfg.why.image}
              onChange={(v) => set("why", "image", v)}
            />
            <CardRows
              testId="site-why-items"
              items={cfg.why.items}
              onChange={(v) => set("why", "items", v)}
            />
          </Section>

          <Section
            title="Services"
            sub="What you build, plus the what's-included list on the Services page"
            testId="site-services"
            right={
              <Toggle
                id="services"
                value={cfg.services.enabled}
                onChange={(v) => set("services", "enabled", v)}
              />
            }
            {...panel("services")}
          >
            <Field label="Heading">
              <Input
                data-testid="site-services-heading"
                value={cfg.services.heading}
                onChange={(e) => set("services", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <CardRows
              testId="site-services-items"
              items={cfg.services.items}
              fields={["title", "body", "image"]}
              onChange={(v) => set("services", "items", v)}
            />
            <Field label="Included list heading">
              <Input
                data-testid="site-services-included-heading"
                value={cfg.services.included_heading}
                onChange={(e) =>
                  set("services", "included_heading", e.target.value)
                }
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <TextList
              testId="site-services-included"
              items={cfg.services.included}
              placeholder="Something you always include"
              onChange={(v) => set("services", "included", v)}
            />
          </Section>

          <Section
            title="Brands we stock"
            sub="Full-width dark band. Upload each brand's logo (shown in white) or leave the name as text."
            testId="site-suppliers"
            right={
              <Toggle
                id="suppliers"
                value={cfg.suppliers.enabled}
                onChange={(v) => set("suppliers", "enabled", v)}
              />
            }
            {...panel("suppliers")}
          >
            <Field label="Eyebrow label" hint="Small line above the heading">
              <Input
                data-testid="site-suppliers-heading"
                value={cfg.suppliers.heading}
                onChange={(e) => set("suppliers", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading">
              <Input
                data-testid="site-suppliers-title"
                value={cfg.suppliers.title || ""}
                onChange={(e) => set("suppliers", "title", e.target.value)}
                placeholder="The names our customers trust"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Sub text" hint="Optional short line under the heading">
              <Textarea
                data-testid="site-suppliers-sub"
                rows={2}
                value={cfg.suppliers.sub || ""}
                onChange={(e) => set("suppliers", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="Brand landing pages"
              hint="Each published brand gets its own page at /brands/<name> listing every slab and remnant you stock from it; logos in the band link to it."
            >
              <span className="mt-2 flex items-center gap-3 text-sm text-zinc-300">
                <Switch
                  data-testid="site-suppliers-brand-pages"
                  checked={cfg.suppliers.brand_pages !== false}
                  onCheckedChange={(v) => set("suppliers", "brand_pages", v)}
                />
                {cfg.suppliers.brand_pages !== false ? "On" : "Off"}
              </span>
            </Field>
            <Field
              label="Showroom map on special-order pages"
              hint="Special-order colors show a “See it in person” box with the supplier's showroom. When on, a small embedded map of that address appears in the box so shoppers can see how far it is. Address, hours and notes come from each supplier's record."
            >
              <span className="mt-2 flex items-center gap-3 text-sm text-zinc-300">
                <Switch
                  data-testid="site-suppliers-showroom-map"
                  checked={cfg.suppliers.showroom_map !== false}
                  onCheckedChange={(v) => set("suppliers", "showroom_map", v)}
                />
                {cfg.suppliers.showroom_map !== false ? "On" : "Off"}
              </span>
            </Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Columns" hint="Logo cells per row on desktop">
                <select
                  data-testid="site-suppliers-columns"
                  value={cfg.suppliers.columns || 4}
                  onChange={(e) => set("suppliers", "columns", Number(e.target.value))}
                  className="mt-1 h-11 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                >
                  {[2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </Field>
              <Field label="Logo color" hint="Force every logo to one color">
                <select
                  data-testid="site-suppliers-logo-color"
                  value={cfg.suppliers.logo_color || "white"}
                  onChange={(e) => set("suppliers", "logo_color", e.target.value)}
                  className="mt-1 h-11 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                >
                  <option value="white">White</option>
                  <option value="black">Black</option>
                  <option value="original">Original colors</option>
                </select>
              </Field>
              <Field label="Background color" hint="Section background — blank keeps the dark default">
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    data-testid="site-suppliers-bg-color"
                    value={cfg.suppliers.bg_color || "#1a1a18"}
                    onChange={(e) => set("suppliers", "bg_color", e.target.value)}
                    className="h-11 w-14 cursor-pointer rounded border border-zinc-800 bg-zinc-950 p-1"
                  />
                  <Input
                    data-testid="site-suppliers-bg-color-hex"
                    value={cfg.suppliers.bg_color || ""}
                    onChange={(e) => set("suppliers", "bg_color", e.target.value)}
                    placeholder="#1a1a18"
                    className="font-mono-data h-11 flex-1 border-zinc-800 bg-zinc-950"
                  />
                </div>
              </Field>
            </div>
            <p className="text-[11px] text-zinc-500">
              Upload each brand's logo (a transparent PNG or SVG works best). Logos are
              recoloured to the “Logo color” above — white by default. Brands without a
              logo show their name in the same colour.
            </p>
            <CardRows
              testId="site-suppliers-items"
              items={cfg.suppliers.items || []}
              fields={["title", "image"]}
              onChange={(v) => set("suppliers", "items", v)}
            />
          </Section>

          <Section
            title="Collections — Brands"
            sub="Add, edit, reorder or remove the brand pages under Collections"
            testId="site-brands-cms"
            {...panel("brands")}
          >
            <p className="text-xs text-zinc-500">
              Each brand gets a page at /collections/brands/&lt;slug&gt; and a
              link in the Collections menu. Upload a hero image, list features
              and collection cards, and point the main button wherever you like.
            </p>
            <BrandsEditor
              brands={cfg.brands}
              onChange={(v) => setCfg({ ...cfg, brands: v })}
            />
          </Section>

          <Section
            title="Gallery"
            sub="Finished-job photos you tick on the Completion photos page"
            testId="site-gallery"
            right={
              <Toggle
                id="gallery"
                value={cfg.gallery.enabled}
                onChange={(v) => set("gallery", "enabled", v)}
              />
            }
            {...panel("gallery")}
          >
            <Field label="Home page heading">
              <Input
                data-testid="site-gallery-heading"
                value={cfg.gallery.heading}
                onChange={(e) => set("gallery", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading size">
              <div className="mt-2 flex items-center gap-4">
                <input
                  type="range"
                  min="50"
                  max="160"
                  step="5"
                  data-testid="site-gallery-heading-scale"
                  value={cfg.gallery.heading_scale ?? 100}
                  onChange={(e) =>
                    set("gallery", "heading_scale", Number(e.target.value))
                  }
                  className="h-1 flex-1 cursor-pointer accent-indigo-400"
                />
                <span
                  className="w-14 text-right text-sm text-zinc-400"
                  data-testid="site-gallery-heading-scale-value"
                >
                  {cfg.gallery.heading_scale ?? 100}%
                </span>
              </div>
            </Field>
            <Field label="Blurb beside the heading">
              <Textarea
                data-testid="site-gallery-sub"
                rows={2}
                value={cfg.gallery.sub}
                onChange={(e) => set("gallery", "sub", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Button under the photos">
              <Input
                data-testid="site-gallery-cta"
                value={cfg.gallery.cta_label}
                onChange={(e) => set("gallery", "cta_label", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Gallery page heading">
              <Input
                data-testid="site-gallery-page-heading"
                value={cfg.gallery.page_heading}
                onChange={(e) => set("gallery", "page_heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="Hero strip projects"
              hint="Pick up to 4 finished projects to feature in the homepage hero photo strip. Leave all off to auto-show your most recent work. Numbers show the order they appear."
            >
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="site-gallery-strip-picker">
                {stripProjects.length === 0 ? (
                  <p className="col-span-full text-sm text-zinc-500">
                    No finished projects yet — tick photos on the Completion photos page first.
                  </p>
                ) : (
                  stripProjects.map((p) => {
                    const ids = cfg.gallery.strip_ids || [];
                    const pos = ids.indexOf(p.id);
                    const on = pos !== -1;
                    const atMax = ids.length >= 4 && !on;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`site-gallery-strip-${p.id}`}
                        aria-pressed={on}
                        disabled={atMax}
                        onClick={() => {
                          const cur = cfg.gallery.strip_ids || [];
                          const next = cur.includes(p.id)
                            ? cur.filter((x) => x !== p.id)
                            : cur.length >= 4
                              ? cur
                              : [...cur, p.id];
                          set("gallery", "strip_ids", next);
                        }}
                        className={`group relative aspect-[4/3] overflow-hidden border-2 transition-colors ${
                          on ? "border-indigo-500" : "border-transparent hover:border-zinc-600"
                        } ${atMax ? "cursor-not-allowed opacity-40" : ""}`}
                        title={p.title || p.color}
                      >
                        <img
                          src={`${API}/public/showroom/photo/${p.thumb}`}
                          alt={p.title || ""}
                          className="h-full w-full object-cover"
                        />
                        {on ? (
                          <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-[10px] font-bold text-white">
                            {pos + 1}
                          </span>
                        ) : null}
                        <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-1 text-[10px] text-white">
                          {p.title || p.color}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </Field>
          </Section>

          <Section
            title="Testimonials"
            sub="Customer quotes in the dark review slider (home page + About page)"
            testId="site-testimonials"
            right={
              <Toggle
                id="testimonials"
                value={cfg.testimonials.enabled}
                onChange={(v) => set("testimonials", "enabled", v)}
              />
            }
            {...panel("testimonials")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Small line">
                <Input
                  data-testid="site-testimonials-micro"
                  value={cfg.testimonials.micro || ""}
                  onChange={(e) => set("testimonials", "micro", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Heading">
                <Input
                  data-testid="site-testimonials-heading"
                  value={cfg.testimonials.heading}
                  onChange={(e) => set("testimonials", "heading", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <CardRows
              testId="site-testimonials-items"
              items={cfg.testimonials.items}
              fields={["title", "body", "source"]}
              onChange={(v) => set("testimonials", "items", v)}
            />

            <div
              className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4"
              data-testid="site-google-reviews"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold">Google reviews</h3>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    Pulls your latest Google reviews into the slider automatically (refreshed
                    daily). Needs a Google Places API key and your Place ID.
                  </p>
                </div>
                <Toggle
                  id="testimonials-google"
                  label="Include Google reviews"
                  value={cfg.testimonials.google_enabled !== false}
                  onChange={(v) => set("testimonials", "google_enabled", v)}
                />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field
                  label="Places API key"
                  hint={
                    cfg.google?.places_api_key_set
                      ? "A key is saved — paste a new one to replace it"
                      : "Google Cloud Console → APIs & Services → Credentials → API key (enable Places API (New))"
                  }
                >
                  <div className="mt-1 flex gap-2">
                    <Input
                      type="password"
                      autoComplete="off"
                      data-testid="site-google-places-key"
                      value={cfg.google?.places_api_key || ""}
                      onChange={(e) => set("google", "places_api_key", e.target.value)}
                      placeholder={cfg.google?.places_api_key_set ? "•••••••••••• (saved)" : "AIza…"}
                      className="font-mono-data h-11 border-zinc-800 bg-zinc-950"
                    />
                    {cfg.google?.places_api_key_set && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={removeGoogleKey}
                        data-testid="site-google-places-key-remove"
                        className="h-11 border-zinc-700 text-[11px] uppercase tracking-widest"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </Field>
                <Field label="Place ID" hint="ChIJ… — same one the review button uses (Google tools section)">
                  <Input
                    data-testid="site-google-reviews-place"
                    value={cfg.google?.place_id || ""}
                    onChange={(e) => set("google", "place_id", e.target.value)}
                    className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                  />
                </Field>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={syncGoogleReviews}
                  disabled={syncingGoogle}
                  data-testid="site-google-reviews-sync"
                  className="h-10 bg-indigo-500 text-[11px] uppercase tracking-widest text-white hover:bg-indigo-400"
                >
                  {syncingGoogle ? "Syncing…" : "Save & sync now"}
                </Button>
                <p className="text-xs text-zinc-400" data-testid="site-google-reviews-status">
                  {googleReviews?.error ? (
                    <span className="text-amber-300">{googleReviews.error}</span>
                  ) : googleReviews?.synced_at ? (
                    <>
                      {googleReviews.reviews} review{googleReviews.reviews === 1 ? "" : "s"}
                      {googleReviews.rating ? ` · ${Number(googleReviews.rating).toFixed(1)}★` : ""}
                      {googleReviews.count ? ` (${googleReviews.count} on Google)` : ""} · last
                      synced {new Date(googleReviews.synced_at).toLocaleString()}
                    </>
                  ) : (
                    "Not synced yet"
                  )}
                </p>
              </div>
            </div>
          </Section>

          <Section
            title="About page"
            sub="Your story and the photo beside it"
            testId="site-about"
            {...panel("about")}
          >
            <Field label="Eyebrow" hint="Small pill above the heading">
              <Input
                data-testid="site-about-eyebrow"
                value={cfg.about.eyebrow || ""}
                onChange={(e) => set("about", "eyebrow", e.target.value)}
                placeholder="About us"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Heading">
              <Input
                data-testid="site-about-heading"
                value={cfg.about.heading}
                onChange={(e) => set("about", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="First paragraph — hero intro" hint="Rich text: bold, links, lists, headings. Shows under the About hero heading.">
              <div className="mt-1">
                <RichTextEditor
                  dataTestId="site-about-body"
                  value={cfg.about.body}
                  onChange={(v) => set("about", "body", v)}
                />
              </div>
            </Field>
            <Field label="Second paragraph — your story" hint="Rich text: bold, links, lists, headings. Shows in the “Our story” block.">
              <div className="mt-1">
                <RichTextEditor
                  dataTestId="site-about-body2"
                  value={cfg.about.body2}
                  onChange={(v) => set("about", "body2", v)}
                />
              </div>
            </Field>
            <ImageField
              label="Photo"
              testId="site-about-image"
              value={cfg.about.image}
              onChange={(v) => set("about", "image", v)}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Highlight stat 1 — number">
                <Input
                  data-testid="site-about-stat1-value"
                  value={cfg.about.stat1_value || ""}
                  onChange={(e) => set("about", "stat1_value", e.target.value)}
                  placeholder="150+"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Highlight stat 1 — label">
                <Input
                  data-testid="site-about-stat1-label"
                  value={cfg.about.stat1_label || ""}
                  onChange={(e) => set("about", "stat1_label", e.target.value)}
                  placeholder="Countertops installed"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Highlight stat 2 — number">
                <Input
                  data-testid="site-about-stat2-value"
                  value={cfg.about.stat2_value || ""}
                  onChange={(e) => set("about", "stat2_value", e.target.value)}
                  placeholder="4.9★"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Highlight stat 2 — label">
                <Input
                  data-testid="site-about-stat2-label"
                  value={cfg.about.stat2_label || ""}
                  onChange={(e) => set("about", "stat2_label", e.target.value)}
                  placeholder="Average customer review"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Years-experience number"
                hint="Big gold number on the 'concept to completion' strip"
              >
                <Input
                  data-testid="site-about-exp-years"
                  value={cfg.about.exp_years || ""}
                  onChange={(e) => set("about", "exp_years", e.target.value)}
                  placeholder="25+"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Years-experience label">
                <Input
                  data-testid="site-about-exp-years-label"
                  value={cfg.about.exp_years_label || ""}
                  onChange={(e) =>
                    set("about", "exp_years_label", e.target.value)
                  }
                  placeholder="Years crafting stone in St. Louis"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Value callout 1">
                <Input
                  data-testid="site-about-exp-note1"
                  value={cfg.about.exp_note1 || ""}
                  onChange={(e) => set("about", "exp_note1", e.target.value)}
                  placeholder="Customized design solutions"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Value callout 2">
                <Input
                  data-testid="site-about-exp-note2"
                  value={cfg.about.exp_note2 || ""}
                  onChange={(e) => set("about", "exp_note2", e.target.value)}
                  placeholder="Premium materials & finishes"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field
              label="Marquee phrases"
              hint="The scrolling band under the stats. Separate each phrase with a · (middle dot)."
            >
              <Input
                data-testid="site-about-marquee"
                value={cfg.about.marquee || ""}
                onChange={(e) => set("about", "marquee", e.target.value)}
                placeholder="Vein-matched seams · Cut in our own shop · Installed by our crew"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="Styles section eyebrow"
              hint="The 'our work' carousel uses your Projects photos (edit them in the Projects section)."
            >
              <Input
                data-testid="site-about-styles-eyebrow"
                value={cfg.about.styles_eyebrow || ""}
                onChange={(e) => set("about", "styles_eyebrow", e.target.value)}
                placeholder="Our work"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Styles section heading">
              <Input
                data-testid="site-about-styles-heading"
                value={cfg.about.styles_heading || ""}
                onChange={(e) => set("about", "styles_heading", e.target.value)}
                placeholder="We've worked on stone in every kind of home"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </Section>

          <Section
            title="Showroom location"
            sub="Map of the showroom with hours and a directions link (uses the address in Company settings)"
            testId="site-location"
            right={
              <Toggle
                id="location"
                value={cfg.location?.enabled !== false}
                onChange={(v) => set("location", "enabled", v)}
              />
            }
            {...panel("location")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Small line">
                <Input
                  data-testid="site-location-micro"
                  value={cfg.location?.micro || ""}
                  onChange={(e) => set("location", "micro", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Button label">
                <Input
                  data-testid="site-location-cta"
                  value={cfg.location?.cta_label || ""}
                  onChange={(e) => set("location", "cta_label", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Heading">
              <Input
                data-testid="site-location-heading"
                value={cfg.location?.heading || ""}
                onChange={(e) => set("location", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Copy">
              <Textarea
                data-testid="site-location-body"
                rows={3}
                value={cfg.location?.body || ""}
                onChange={(e) => set("location", "body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
          </Section>

          <Section
            title="Contact / quote form"
            sub="Address, phone and email beside the quote form, near the bottom of the page"
            testId="site-cta"
            right={
              <Toggle
                id="cta"
                value={cfg.cta.enabled}
                onChange={(v) => set("cta", "enabled", v)}
              />
            }
            {...panel("cta")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Small line">
                <Input
                  data-testid="site-cta-micro"
                  value={cfg.cta.micro || ""}
                  onChange={(e) => set("cta", "micro", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Button label">
                <Input
                  data-testid="site-cta-label"
                  value={cfg.cta.cta_label || ""}
                  onChange={(e) => set("cta", "cta_label", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
            <Field label="Heading">
              <Input
                data-testid="site-cta-heading"
                value={cfg.cta.heading}
                onChange={(e) => set("cta", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Copy">
              <Textarea
                data-testid="site-cta-body"
                rows={3}
                value={cfg.cta.body}
                onChange={(e) => set("cta", "body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Background photo"
              testId="site-cta-image"
              value={cfg.cta.image || ""}
              onChange={(v) => set("cta", "image", v)}
            />
          </Section>

          <Section
            title="Contact page"
            sub="Hero background image, heading, text, the contact tiles and opening hours"
            testId="site-contact"
            {...panel("contact")}
          >
            <ImageField
              label="Hero background image"
              testId="site-contact-hero-image"
              value={cfg.contact?.hero_image}
              onChange={(v) => set("contact", "hero_image", v)}
            />
            <Field
              label="Hero image darkness"
              hint="How dark the overlay sits over your background image — higher makes the text easier to read"
            >
              <div className="mt-2 flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="90"
                  step="5"
                  data-testid="site-contact-hero-overlay"
                  value={cfg.contact?.hero_overlay ?? 60}
                  onChange={(e) =>
                    set("contact", "hero_overlay", Number(e.target.value))
                  }
                  className="h-2 flex-1 cursor-pointer accent-[#c39e6d]"
                />
                <span
                  data-testid="site-contact-hero-overlay-val"
                  className="font-mono-data w-12 text-right text-sm text-zinc-300"
                >
                  {cfg.contact?.hero_overlay ?? 60}%
                </span>
              </div>
            </Field>
            <Field label="Left panel heading">
              <Input
                data-testid="site-contact-heading"
                value={cfg.contact?.heading || ""}
                onChange={(e) => set("contact", "heading", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Left panel text">
              <Textarea
                data-testid="site-contact-body"
                rows={2}
                value={cfg.contact?.body || ""}
                onChange={(e) => set("contact", "body", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="Contact tiles"
              hint="The boxes under the heading. Address, phone, email and hours pull from Settings → My Company unless you type something here; rename, reorder, hide, or add your own (e.g. Text us, WhatsApp, Parking)."
            >
              <div className="mt-2">
                <ContactTilesEditor
                  tiles={cfg.contact?.tiles}
                  onChange={(v) => set("contact", "tiles", v)}
                />
              </div>
            </Field>
            <Field
              label="Contact icon color"
              hint="Color of the small square icon chips beside your contact details"
            >
              <div className="mt-1 flex items-center gap-3">
                <input
                  type="color"
                  data-testid="site-contact-color"
                  value={cfg.contact?.panel_color || "#1c1917"}
                  onChange={(e) => set("contact", "panel_color", e.target.value)}
                  className="h-11 w-14 cursor-pointer rounded border border-zinc-800 bg-zinc-950 p-1"
                />
                <Input
                  data-testid="site-contact-color-hex"
                  value={cfg.contact?.panel_color || ""}
                  onChange={(e) => set("contact", "panel_color", e.target.value)}
                  placeholder="#1c1917"
                  className="font-mono-data h-11 w-40 border-zinc-800 bg-zinc-950"
                />
              </div>
            </Field>
            <Field label="Hours label">
              <Input
                data-testid="site-contact-hours-label"
                value={cfg.contact?.hours_label || ""}
                onChange={(e) => set("contact", "hours_label", e.target.value)}
                placeholder="Hours"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field
              label="Hours override"
              hint="Leave these empty — your opening hours now come from Settings → My Company, day by day. Only fill these in if you want different wording on the website."
            >
              <TextList
                testId="site-contact-hours"
                items={cfg.contact?.hours || []}
                placeholder="Mon–Fri 08:00–17:00"
                onChange={(v) => set("contact", "hours", v)}
              />
            </Field>

          </Section>

          <Section
            title="Resources page"
            sub="Manufacturer warranties, care guides and technical documents — shown under Company → Resources"
            testId="site-resources"
            {...panel("resources")}
          >
            <ResourcesEditor />
          </Section>

          <Section
            title="FAQs"
            sub="Grouped question-and-answer accordions shown at the bottom of the Resources page"
            testId="site-faqs"
            right={
              <Toggle
                id="faqs"
                value={cfg.faqs?.enabled}
                onChange={(v) => set("faqs", "enabled", v)}
              />
            }
            {...panel("faqs")}
          >
            <Field label="Heading">
              <Input
                data-testid="site-faqs-heading"
                value={cfg.faqs?.heading || ""}
                onChange={(e) => set("faqs", "heading", e.target.value)}
                placeholder="Frequently asked questions"
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Question groups" hint="Each group shows its title on the left and its questions as an accordion on the right.">
              <FaqGroupsEditor
                groups={cfg.faqs?.groups || []}
                onChange={(v) => set("faqs", "groups", v)}
              />
            </Field>
          </Section>

          <Section
            title="Edges page — 3D stone"
            sub="The stone wrapped onto the 3D edge previews under Collections → Edges. With no photo the site draws a Taj Mahal quartzite look, or borrows the photo of a slab on the racks whose color matches the name"
            testId="site-edges"
            {...panel("edges")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Stone name"
                hint="Shown in the viewer caption and used to find a matching slab in inventory"
              >
                <Input
                  data-testid="site-edges-stone-name"
                  value={cfg.edges.stone_name}
                  onChange={(e) => set("edges", "stone_name", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <ImageField
                label="Stone photo (flat, evenly lit close-up works best)"
                testId="site-edges-stone-image"
                value={cfg.edges.stone_image}
                onChange={(v) => set("edges", "stone_image", v)}
              />
            </div>
            <Field
              label="Edge profiles"
              hint="The edges shown on the public Edges page (Collections → Edges), the 3D viewer and quote drawings. Toggle any off, reorder, rename, or add your own reusing a shape."
            >
              <EdgeProfilesEditor
                value={cfg.edges?.profiles || []}
                onChange={(v) => set("edges", "profiles", v)}
              />
            </Field>
          </Section>

          <Section
            title="Inventory categories"
            sub="Photo cards shown under the breadcrumbs on the Inventory page. Pick an image per material — with none set the site uses a slab photo of that material."
            testId="site-inventory"
            {...panel("inventory")}
          >
            <InventoryCategoriesEditor
              value={cfg.inventory?.category_images || {}}
              onChange={(v) => set("inventory", "category_images", v)}
            />
          </Section>

          <Section
            title="Footer"
            sub="Dark footer — socials, contact, link columns, the pill button and the giant background wordmark"
            testId="site-footer"
            {...panel("footer")}
          >
            <a
              href={`${window.location.origin}/`}
              target="_blank"
              rel="noreferrer"
              data-testid="site-footer-preview-link"
              className="mb-1 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-indigo-300 transition-colors hover:text-indigo-200"
            >
              <Globe size={13} weight="bold" /> View live footer
            </a>
            <Field
              label="Tagline"
              hint="Short line shown at the bottom-left of the footer"
            >
              <Textarea
                data-testid="site-footer-tagline"
                rows={2}
                value={cfg.footer.tagline}
                onChange={(e) => set("footer", "tagline", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Instagram link">
                <Input
                  data-testid="site-footer-instagram"
                  value={cfg.footer.instagram}
                  onChange={(e) => set("footer", "instagram", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="X (Twitter) link">
                <Input
                  data-testid="site-footer-twitter"
                  value={cfg.footer.twitter || ""}
                  onChange={(e) => set("footer", "twitter", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="YouTube link">
                <Input
                  data-testid="site-footer-youtube"
                  value={cfg.footer.youtube || ""}
                  onChange={(e) => set("footer", "youtube", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Facebook link">
                <Input
                  data-testid="site-footer-facebook"
                  value={cfg.footer.facebook}
                  onChange={(e) => set("footer", "facebook", e.target.value)}
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Google reviews link">
                <Input
                  data-testid="site-footer-google"
                  value={cfg.footer.google_reviews}
                  onChange={(e) =>
                    set("footer", "google_reviews", e.target.value)
                  }
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Button label" hint="The pill button on the divider">
                <Input
                  data-testid="site-footer-cta-label"
                  value={cfg.footer.cta_label || ""}
                  onChange={(e) => set("footer", "cta_label", e.target.value)}
                  placeholder="Get a free quote"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Button link">
                <Input
                  data-testid="site-footer-cta-href"
                  value={cfg.footer.cta_href || ""}
                  onChange={(e) => set("footer", "cta_href", e.target.value)}
                  placeholder="/contact"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>

            <Field
              label="Link columns"
              hint="Each column has a heading and a list of links. Use an internal path like /inventory, or a full https:// link."
            >
              <div className="mt-2">
                <ColumnsEditor
                  testId="site-footer-columns"
                  columns={cfg.footer.columns || []}
                  onChange={(v) => set("footer", "columns", v)}
                />
              </div>
            </Field>

            <Field
              label="Legal links"
              hint="Shown small at the bottom-right, e.g. Terms, Privacy"
            >
              <div className="mt-2">
                <LinkRows
                  testId="site-footer-legal"
                  items={cfg.footer.legal_links || []}
                  onChange={(v) => set("footer", "legal_links", v)}
                />
              </div>
            </Field>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-zinc-400">
                  Newsletter signup
                </div>
                <Switch
                  data-testid="site-footer-show_newsletter"
                  checked={cfg.footer.show_newsletter !== false}
                  onCheckedChange={(v) => set("footer", "show_newsletter", v)}
                />
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <Field label="Heading">
                  <Input
                    data-testid="site-footer-newsletter-heading"
                    value={cfg.footer.newsletter_heading || ""}
                    onChange={(e) =>
                      set("footer", "newsletter_heading", e.target.value)
                    }
                    placeholder="Join the list"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-900"
                  />
                </Field>
                <Field label="Button label">
                  <Input
                    data-testid="site-footer-newsletter-button"
                    value={cfg.footer.newsletter_button || ""}
                    onChange={(e) =>
                      set("footer", "newsletter_button", e.target.value)
                    }
                    placeholder="Subscribe"
                    className="mt-1 h-11 border-zinc-800 bg-zinc-900"
                  />
                </Field>
              </div>
              <Field label="Subtext" hint="Short line under the heading">
                <Textarea
                  data-testid="site-footer-newsletter-subtext"
                  rows={2}
                  value={cfg.footer.newsletter_subtext || ""}
                  onChange={(e) =>
                    set("footer", "newsletter_subtext", e.target.value)
                  }
                  className="mt-1 border-zinc-800 bg-zinc-900"
                />
              </Field>
              <Field
                label="Notify email"
                hint="We'll email this address whenever someone new subscribes"
              >
                <Input
                  data-testid="site-footer-newsletter-notify"
                  value={cfg.footer.notify_email || ""}
                  onChange={(e) => set("footer", "notify_email", e.target.value)}
                  placeholder="you@brionigranite.com"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-900"
                />
              </Field>
              <div className="mt-4">
                <NewsletterSubscribers />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Background wordmark"
                hint="Giant text bleeding off the bottom. Leave blank to use your shop name."
              >
                <Input
                  data-testid="site-footer-watermark"
                  value={cfg.footer.watermark || ""}
                  onChange={(e) => set("footer", "watermark", e.target.value)}
                  placeholder="Brioni Granite"
                  className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3 sm:mt-6">
                <span className="text-xs text-zinc-400">
                  Show background wordmark
                </span>
                <Switch
                  data-testid="site-footer-show_watermark"
                  checked={cfg.footer.show_watermark !== false}
                  onCheckedChange={(v) => set("footer", "show_watermark", v)}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {[
                ["show_portal_link", "Customer portal link"],
                ["show_staff_login", "Staff sign-in link"],
              ].map(([key, label]) => (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3"
                >
                  <span className="text-xs text-zinc-400">{label}</span>
                  <Switch
                    data-testid={`site-footer-${key}`}
                    checked={!!cfg.footer[key]}
                    onCheckedChange={(v) => set("footer", key, v)}
                  />
                </div>
              ))}
            </div>
          </Section>

          <Section
            title="Search & sharing"
            sub="What Google and social previews show"
            testId="site-seo"
            {...panel("seo")}
          >
            <Field label="Page title" hint="Around 60 characters">
              <Input
                data-testid="site-seo-title"
                value={cfg.seo.title}
                onChange={(e) => set("seo", "title", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <Field label="Description" hint="Around 155 characters">
              <Textarea
                data-testid="site-seo-description"
                rows={3}
                value={cfg.seo.description}
                onChange={(e) => set("seo", "description", e.target.value)}
                className="mt-1 border-zinc-800 bg-zinc-950"
              />
            </Field>
            <ImageField
              label="Share picture"
              testId="site-seo-og"
              value={cfg.seo.og_image}
              onChange={(v) => set("seo", "og_image", v)}
            />
            <Field
              label="Keywords"
              hint="Comma-separated; helps AI search understand your focus"
            >
              <Input
                data-testid="site-seo-keywords"
                value={cfg.seo.keywords || ""}
                onChange={(e) => set("seo", "keywords", e.target.value)}
                className="mt-1 h-11 border-zinc-800 bg-zinc-950"
                placeholder="granite countertops st louis, quartz fabricator, quartzite slabs"
              />
            </Field>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2.5">
              <div>
                <div className="text-sm font-semibold text-zinc-200">
                  AI &amp; search optimization
                </div>
                <div className="text-xs text-zinc-500">
                  Welcome AI answer engines (ChatGPT, Perplexity, Google AI), serve an
                  llms.txt guide, and emit rich-snippet hints.
                </div>
              </div>
              <Toggle
                id="seo-ai"
                value={cfg.seo.ai_optimized !== false}
                onChange={(v) => set("seo", "ai_optimized", v)}
                label={cfg.seo.ai_optimized !== false ? "On" : "Off"}
              />
            </div>
          </Section>

          <Section
            title="Analytics"
            sub="Paste your tracking IDs and the site loads them for you"
            testId="site-analytics"
            {...panel("analytics")}
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Google Analytics ID" hint="G-XXXXXXXXXX">
                <Input
                  data-testid="site-analytics-ga"
                  value={cfg.analytics.ga_id}
                  onChange={(e) => set("analytics", "ga_id", e.target.value)}
                  className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Google Tag Manager" hint="GTM-XXXXXXX">
                <Input
                  data-testid="site-analytics-gtm"
                  value={cfg.analytics.gtm_id}
                  onChange={(e) => set("analytics", "gtm_id", e.target.value)}
                  className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
              <Field label="Meta pixel ID">
                <Input
                  data-testid="site-analytics-pixel"
                  value={cfg.analytics.meta_pixel}
                  onChange={(e) =>
                    set("analytics", "meta_pixel", e.target.value)
                  }
                  className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Google tools"
            sub="Put the website on Google: Search Console, Analytics, Ads, Business Profile and the structured data that ties them together"
            testId="site-google"
            {...panel("google")}
          >
            {(() => {
              const g = cfg.google || {};
              const a = cfg.analytics || {};
              const origin =
                (g.site_url || "").trim().replace(/\/$/, "") ||
                window.location.origin;
              const sitemap = `${origin}/api/public/sitemap.xml`;
              const robots = `${origin}/robots.txt`;
              const copy = (text, what) =>
                navigator.clipboard
                  ?.writeText(text)
                  .then(() => toast.success(`${what} copied`));
              const checks = [
                ["Search Console verified", !!(g.site_verification || "").trim(), "google-check-verification"],
                ["Analytics (GA4)", !!(a.ga_id || "").trim(), "google-check-ga"],
                ["Tag Manager", !!(a.gtm_id || "").trim(), "google-check-gtm"],
                ["Google Ads tag", !!(g.ads_id || "").trim(), "google-check-ads"],
                ["Business Profile linked", !!(g.business_profile_url || g.place_id || "").trim(), "google-check-gbp"],
                ["Structured data", g.structured_data !== false, "google-check-schema"],
              ];
              const gbpUrl =
                (g.business_profile_url || "").trim() ||
                ((g.place_id || "").trim()
                  ? `https://www.google.com/maps/place/?q=place_id:${(g.place_id || "").trim()}`
                  : "https://business.google.com/");
              const tools = [
                ["Search Console", "https://search.google.com/search-console", "Verify ownership, submit the sitemap, see search traffic"],
                ["Google Analytics", "https://analytics.google.com/", "GA4 property → Data streams → Measurement ID (G-…)"],
                ["Tag Manager", "https://tagmanager.google.com/", "Optional container for extra tags (GTM-…)"],
                ["Business Profile", gbpUrl, (g.business_profile_url || g.place_id || "").trim() ? "Opens your linked listing" : "Add your Business Profile link below, then this opens your listing"],
                ["Google Ads", "https://ads.google.com/", "Conversion tag (AW-…) for campaigns"],
                ["Rich Results Test", `https://search.google.com/test/rich-results?url=${encodeURIComponent(origin)}`, "Check the LocalBusiness data Google reads from the site"],
                ["PageSpeed Insights", `https://pagespeed.web.dev/analysis?url=${encodeURIComponent(origin)}`, "Core Web Vitals for the homepage"],
                ["Place ID finder", "https://developers.google.com/maps/documentation/places/web-service/place-id", "Look up the Place ID for the showroom"],
              ];
              return (
                <div className="space-y-6">
                  <div className="flex flex-wrap gap-2" data-testid="google-checklist">
                    {checks.map(([label, on, tid]) => (
                      <span
                        key={tid}
                        data-testid={tid}
                        data-on={on ? "1" : "0"}
                        className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${
                          on
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                            : "border-zinc-800 bg-zinc-950 text-zinc-500"
                        }`}
                      >
                        {on ? "✓ " : "○ "}
                        {label}
                      </span>
                    ))}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Public site URL" hint="Canonical address, e.g. https://www.brionigranite.com — used in the sitemap and structured data">
                      <Input
                        data-testid="site-google-url"
                        value={g.site_url || ""}
                        onChange={(e) => set("google", "site_url", e.target.value)}
                        placeholder={window.location.origin}
                        className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                      />
                    </Field>
                    <Field label="Search Console verification" hint='Search Console → Settings → Ownership → HTML tag: paste only the content="…" value'>
                      <Input
                        data-testid="site-google-verification"
                        value={g.site_verification || ""}
                        onChange={(e) => set("google", "site_verification", e.target.value)}
                        placeholder="abc123…"
                        className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                      />
                    </Field>
                    <Field label="Google Ads tag" hint="AW-XXXXXXXXX (Analytics ID lives in the Analytics section above)">
                      <Input
                        data-testid="site-google-ads"
                        value={g.ads_id || ""}
                        onChange={(e) => set("google", "ads_id", e.target.value)}
                        className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                      />
                    </Field>
                    <Field label="Business Profile link" hint="Share link of the listing (maps.app.goo.gl/… or google.com/maps?cid=…)">
                      <Input
                        data-testid="site-google-gbp"
                        value={g.business_profile_url || ""}
                        onChange={(e) => set("google", "business_profile_url", e.target.value)}
                        className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                      />
                    </Field>
                    <Field label="Place ID" hint="ChIJ… — lets the review link and map point at the exact listing">
                      <Input
                        data-testid="site-google-place"
                        value={g.place_id || ""}
                        onChange={(e) => set("google", "place_id", e.target.value)}
                        className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                      />
                    </Field>
                    <Field label="Business type" hint="schema.org type Google shows for the listing">
                      <select
                        data-testid="site-google-type"
                        value={g.business_type || "HomeAndConstructionBusiness"}
                        onChange={(e) => set("google", "business_type", e.target.value)}
                        className="mt-1 h-11 w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 text-sm text-zinc-200"
                      >
                        {["HomeAndConstructionBusiness", "LocalBusiness", "Store", "HomeGoodsStore", "GeneralContractor"].map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Field label="Price range" hint="$ – $$$$">
                        <Input
                          data-testid="site-google-price"
                          value={g.price_range || ""}
                          onChange={(e) => set("google", "price_range", e.target.value)}
                          className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                        />
                      </Field>
                      <Field label="Latitude">
                        <Input
                          data-testid="site-google-lat"
                          value={g.latitude || ""}
                          onChange={(e) => set("google", "latitude", e.target.value)}
                          className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                        />
                      </Field>
                      <Field label="Longitude">
                        <Input
                          data-testid="site-google-lng"
                          value={g.longitude || ""}
                          onChange={(e) => set("google", "longitude", e.target.value)}
                          className="font-mono-data mt-1 h-11 border-zinc-800 bg-zinc-950"
                        />
                      </Field>
                    </div>
                    <Field label="Other profiles (sameAs)" hint="One per line: Yelp, Houzz, LinkedIn… Socials from the Footer section are included automatically">
                      <Textarea
                        data-testid="site-google-sameas"
                        value={g.same_as || ""}
                        onChange={(e) => set("google", "same_as", e.target.value)}
                        rows={3}
                        className="font-mono-data mt-1 border-zinc-800 bg-zinc-950 text-xs"
                      />
                    </Field>
                  </div>

                  <label className="flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950/60 px-4 py-3">
                    <span>
                      <span className="block text-sm font-semibold text-zinc-200">LocalBusiness structured data</span>
                      <span className="block text-xs text-zinc-500">
                        Publishes name, phone, address, hours, map link and profiles as schema.org JSON-LD so Google can match the site to your Business Profile
                      </span>
                    </span>
                    <Switch
                      data-testid="site-google-schema"
                      checked={g.structured_data !== false}
                      onCheckedChange={(v) => set("google", "structured_data", v)}
                    />
                  </label>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {[["Sitemap (submit in Search Console)", sitemap, "site-google-sitemap"], ["robots.txt", robots, "site-google-robots"]].map(([label, url, tid]) => (
                      <div key={tid} className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <code className="min-w-0 flex-1 truncate text-xs text-indigo-200" data-testid={tid}>{url}</code>
                          <button type="button" data-testid={`${tid}-copy`} onClick={() => copy(url, label)} className="rounded border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-400/50">Copy</button>
                          <a href={url} target="_blank" rel="noreferrer" className="rounded border border-zinc-700 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:border-indigo-400/50">Open</a>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Google consoles</div>
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-testid="google-tools-links">
                      {tools.map(([name, href, sub]) => (
                        <a
                          key={name}
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`google-tool-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3 transition-colors hover:border-indigo-400/50"
                        >
                          <div className="text-sm font-semibold text-zinc-100">{name} ↗</div>
                          <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>
                        </a>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-zinc-600">
                    Business Profile hours, photos and posts are managed in Google's console; this site publishes matching details (name, phone, address, hours) so the two stay consistent. Save, then use the Rich Results Test to confirm Google reads them.
                  </p>
                </div>
              );
            })()}
          </Section>

            <div className="mt-8 flex justify-end">
              <Button
                data-testid="site-settings-save-bottom"
                disabled={saving}
                onClick={save}
                className="h-12 bg-indigo-500 px-8 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <FloppyDisk size={14} weight="bold" className="mr-2" />
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
